import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Handle,
  Position,
  addEdge,
  useNodesState,
  useEdgesState
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { BACKTEST_POLL_INTERVAL_MS, createIdempotencyKey, deleteBacktest, getBacktest, getBacktestCredits, getBacktests, getBacktestErrorMessage, normalizeBacktestResponse, renameBacktest, submitBacktest, TERMINAL_BACKTEST_STATES } from "../api/backtests";

/* =====================================================
   CONFIG
===================================================== */

// palette categories
const PALETTE_GROUPS = {
  universe: { label: "Universe", accent: "#E76F51" },
  fundamentals: { label: "Fundamentals", accent: "#2A9D8F" },
  technicals: { label: "Technicals", accent: "#457B9D" },
  operators: { label: "Operators", accent: "#E9A23B" } // green
};

const BLOCKS = [
  // --- Settings ---
  { type: "setting_max_positions", label: "Max positions", group: "setting" },
  { type: "setting_initial_capital", label: "Initial capital", group: "setting" },
  { type: "setting_min_cash_percent", label: "Min cash %", group: "setting" },
  { type: "setting_max_position_size_percent", label: "Max position size %", group: "setting" },
  { type: "setting_rebalance_days", label: "Rebalance days", group: "setting" },
  { type: "setting_transaction_fee", label: "Transaction fee", group: "setting" },
  { type: "setting_target_positions", label: "Target positions", group: "setting" },
  { type: "setting_allocation", label: "Allocation", group: "setting" },
  { type: "setting_min_holding_days", label: "Minimum holding days", group: "setting" },

  // --- Universe ---
  { type: "top_mcap", label: "Top by market cap", group: "universe" },
  { type: "top_volume", label: "Top by volume", group: "universe" },
  { type: "mcap_min", label: "Market cap >=", group: "universe" },
  { type: "liquidity_min", label: "Volume >=", group: "universe" },
  { type: "price_min", label: "Price >=", group: "universe" },
  { type: "price_max", label: "Price <=", group: "universe" },

  // --- Ranking (fixed blocks, not shown in the palette) ---
  { type: "ranking_variable", label: "Rank by", group: "ranking" },
  { type: "ranking_direction", label: "Order", group: "ranking" },

  // --- Operators (2-arg blocks) ---
  { type: "op_gt", label: ">", group: "operators" },
  { type: "op_gte", label: "≥", group: "operators" },
  { type: "op_lt", label: "<", group: "operators" },
  { type: "op_lte", label: "≤", group: "operators" },
  { type: "op_eq", label: "=", group: "operators" },
  { type: "op_neq", label: "≠", group: "operators" },

  // --- Technical variables (draggable into operator args) ---
  { type: "var_price", label: "Price", group: "technicals" },
  { type: "var_volume", label: "Volume", group: "technicals" },
  { type: "var_sma_50", label: "SMA 50", group: "technicals" },
  { type: "var_sma_200", label: "SMA 200", group: "technicals" },
  { type: "var_rsi", label: "RSI", group: "technicals" },
  { type: "var_macd", label: "MACD", group: "technicals" },

  // --- Fundamental variables (draggable into operator args) ---
  { type: "var_market_cap", label: "Market cap", group: "fundamentals" },
  { type: "var_enterprise_value", label: "Enterprise value", group: "fundamentals" },
  { type: "var_revenue_ttm", label: "Revenue TTM", group: "fundamentals" },
  { type: "var_net_income_ttm", label: "Net income TTM", group: "fundamentals" },
  { type: "var_fcf_ttm", label: "FCF TTM", group: "fundamentals" },
  { type: "var_pe_ttm", label: "P/E TTM", group: "fundamentals" },
  { type: "var_ps_ttm", label: "P/S TTM", group: "fundamentals" },
  { type: "var_pb", label: "P/B", group: "fundamentals" },
  { type: "var_p_fcf_ttm", label: "P/FCF TTM", group: "fundamentals" },
  { type: "var_fcf_yield", label: "FCF yield", group: "fundamentals" },
  { type: "var_roe", label: "ROE", group: "fundamentals" },
  { type: "var_roa", label: "ROA", group: "fundamentals" },
  { type: "var_debt_to_equity", label: "Debt/Equity", group: "fundamentals" },
  { type: "var_revenue_growth_yoy", label: "Revenue growth YoY", group: "fundamentals" },
  { type: "var_net_income_growth_yoy", label: "Net income growth YoY", group: "fundamentals" },
  { type: "var_fcf_growth_yoy", label: "FCF growth YoY", group: "fundamentals" },
  { type: "var_gross_margin", label: "Gross margin", group: "fundamentals" },
  { type: "var_operating_margin", label: "Operating margin", group: "fundamentals" },
  { type: "var_net_margin", label: "Net margin", group: "fundamentals" },

];

const NODE_ACCENTS = {
  universe: "#EF4444",
  signals: "#3B82F6",
  execution: "#F59E0B",
  exit: "#8B5CF6"
};

const isOperator = (type) => type?.startsWith("op_");
const isVariable = (type) => type?.startsWith("var_");
const GRAPH_VARIABLE_LABELS = {
  var_market_cap: "MCap",
  var_enterprise_value: "EV",
  var_revenue_ttm: "Rev TTM",
  var_net_income_ttm: "NI TTM",
  var_debt_to_equity: "D/E",
  var_revenue_growth_yoy: "Rev growth",
  var_net_income_growth_yoy: "NI growth",
  var_fcf_growth_yoy: "FCF growth",
  var_gross_margin: "Gross margin",
  var_operating_margin: "Op margin",
  var_net_margin: "Net margin"
};
const COMPACT_UNIVERSE_NUMBER_TYPES = new Set([
  "top_mcap",
  "top_volume",
  "mcap_min",
  "liquidity_min",
  "price_min",
  "price_max"
]);
const isInput = (type) => type?.startsWith("input_");
const isLogicBlock = (type) => type === "logic_and" || type === "logic_or";
const PERCENT_VARIABLE_TYPES = new Set([
  "var_revenue_growth_yoy", "var_net_income_growth_yoy", "var_fcf_growth_yoy",
  "var_fcf_yield", "var_roe", "var_roa", "var_gross_margin",
  "var_operating_margin", "var_net_margin", "var_fcf_margin", "var_dividend_yield"
]);
const PERCENT_THRESHOLD_BLOCK_TYPES = new Set([
  "cfo_growth", "fcf_growth", "rev_growth", "eps_growth", "dividend_yield", "ebitda_growth",
  "revenue_growth_yoy_over", "net_income_growth_yoy_over", "fcf_growth_yoy_over",
  "gross_margin_over", "fcf_margin_over", "fcf_yield_over", "roe_over", "roa_over",
  "operating_margin_over", "net_margin_over", "roic_over"
]);

const OPERATOR_SYMBOLS = {
  op_gt: ">",
  op_gte: ">=",
  op_lt: "<",
  op_lte: "<=",
  op_eq: "===",
  op_neq: "!==",
  op_and: "&&",
  op_or: "||",
  op_add: "+",
  op_sub: "-",
  op_mul: "*",
  op_div: "/"
};

const LOGIC_SYMBOLS = {
  logic_and: "&&",
  logic_or: "||"
};

const VARIABLE_IDENTIFIERS = {
  var_us_stocks: "usStocks",
  var_sp500: "sp500",
  var_nasdaq100: "nasdaq100",
  var_price: "price",
  var_volume: "volume",
  var_market_cap: "marketCap",
  var_enterprise_value: "enterpriseValue",
  var_revenue_ttm: "revenueTtm",
  var_net_income_ttm: "netIncomeTtm",
  var_fcf_ttm: "freeCashFlowTtm",
  var_pe_ttm: "peTtm",
  var_ps_ttm: "psTtm",
  var_p_fcf_ttm: "pFcfTtm",
  var_fcf_yield: "fcfYield",
  var_roa: "roa",
  var_revenue_growth_yoy: "revenueGrowthYoy",
  var_net_income_growth_yoy: "netIncomeGrowthYoy",
  var_fcf_growth_yoy: "fcfGrowthYoy",
  var_operating_margin: "operatingMargin",
  var_net_margin: "netMargin",
  var_fcf: "fcf",
  var_ocf: "ocf",
  var_ebitda: "ebitda",
  var_net_income: "netIncome",
  var_eps: "eps",
  var_revenue: "revenue",
  var_gross_margin: "grossMargin",
  var_fcf_margin: "fcfMargin",
  var_debt: "debt",
  var_debt_to_equity: "debtToEquity",
  var_interest_coverage: "interestCoverage",
  var_cash: "cash",
  var_mcap: "marketCap",
  var_pe: "pe",
  var_pb: "pb",
  var_pfcf: "pfcf",
  var_roe: "roe",
  var_roic: "roic",
  var_sma_50: "sma50",
  var_sma_200: "sma200",
  var_rsi: "rsi",
  var_macd: "macd",
  var_beta: "beta",
  var_dividend_yield: "dividendYield"
};

const VARIABLE_TO_CODE = {
  var_us_stocks: "universe.usStocks",
  var_sp500: "universe.sp500",
  var_nasdaq100: "universe.nasdaq100",
  var_price: "price.close",
  var_volume: "price.volume",
  var_market_cap: "fundamentals.market_cap",
  var_enterprise_value: "fundamentals.enterprise_value",
  var_revenue_ttm: "fundamentals.revenue_ttm",
  var_net_income_ttm: "fundamentals.net_income_ttm",
  var_fcf_ttm: "fundamentals.free_cash_flow_ttm",
  var_pe_ttm: "fundamentals.pe_ttm",
  var_ps_ttm: "fundamentals.ps_ttm",
  var_p_fcf_ttm: "fundamentals.p_fcf_ttm",
  var_fcf_yield: "fundamentals.fcf_yield",
  var_roa: "fundamentals.roa",
  var_revenue_growth_yoy: "fundamentals.revenue_growth_yoy",
  var_net_income_growth_yoy: "fundamentals.net_income_growth_yoy",
  var_fcf_growth_yoy: "fundamentals.fcf_growth_yoy",
  var_operating_margin: "fundamentals.operating_margin",
  var_net_margin: "fundamentals.net_margin",
  var_fcf: "fundamentals.free_cash_flow_ttm",
  var_ocf: "data.ocf",
  var_ebitda: "data.ebitda",
  var_net_income: "fundamentals.net_income_ttm",
  var_eps: "data.eps",
  var_revenue: "fundamentals.revenue_ttm",
  var_gross_margin: "fundamentals.gross_margin",
  var_fcf_margin: "data.fcfMargin",
  var_debt: "data.debt",
  var_debt_to_equity: "fundamentals.debt_to_equity",
  var_interest_coverage: "data.interestCoverage",
  var_cash: "data.cash",
  var_mcap: "fundamentals.market_cap",
  var_pe: "fundamentals.pe_ttm",
  var_pb: "fundamentals.pb",
  var_pfcf: "fundamentals.p_fcf_ttm",
  var_roe: "fundamentals.roe",
  var_roic: "data.roic",
  var_sma_50: "indicators.sma_50",
  var_sma_200: "indicators.sma_200",
  var_rsi: "indicators.rsi",
  var_macd: "indicators.macd",
  var_beta: "data.beta",
  var_dividend_yield: "data.dividendYield"
};

const BLOCK_FIELDS = {
  setting_max_positions: [{ key: "value", label: "Int", inputType: "int", defaultValue: "20" }],
  setting_initial_capital: [{ key: "value", label: "Int", inputType: "int", defaultValue: "10000" }],
  setting_min_cash_percent: [{ key: "value", label: "%", inputType: "int", defaultValue: "5" }],
  setting_max_position_size_percent: [{ key: "value", label: "%", inputType: "int", defaultValue: "5" }],
  setting_rebalance_days: [{ key: "value", label: "Days", inputType: "int", defaultValue: "30" }],
  setting_transaction_fee: [{ key: "value", label: "Fee", inputType: "number", defaultValue: "0" }],
  setting_target_positions: [{ key: "value", label: "Int", inputType: "int", defaultValue: "20" }],
  setting_allocation: [
    {
      key: "value",
      label: "Mode",
      inputType: "select",
      defaultValue: "equal_weight",
      options: [
        { value: "equal_weight", label: "Equal weight" },
        { value: "market_cap_weight", label: "Market cap weight" },
        { value: "signal_weight", label: "Signal weight" }
      ]
    }
  ],
  setting_min_holding_days: [{ key: "value", label: "Days", inputType: "int", defaultValue: "0" }],
  ranking_variable: [
    {
      key: "value",
      label: "Prefer companies with",
      inputType: "select",
      defaultValue: "fundamentals.fcf_yield",
      options: [
        { value: "fundamentals.fcf_yield", label: "FCF yield" },
        { value: "fundamentals.roe", label: "ROE" },
        { value: "fundamentals.revenue_growth_yoy", label: "Revenue growth" },
        { value: "fundamentals.net_income_growth_yoy", label: "Net income growth" },
        { value: "fundamentals.debt_to_equity", label: "Debt/equity" },
        { value: "fundamentals.operating_margin", label: "Operating margin" },
        { value: "fundamentals.pe_ttm", label: "P/E TTM" },
        { value: "fundamentals.pb", label: "P/B" },
        { value: "fundamentals.ps_ttm", label: "P/S TTM" },
        { value: "fundamentals.market_cap", label: "Market Cap" }
      ]
    }
  ],
  ranking_direction: [
    {
      key: "value",
      label: "Show first",
      inputType: "select",
      defaultValue: "highest_first",
      options: [
        { value: "highest_first", label: "Highest first" },
        { value: "lowest_first", label: "Lowest first" }
      ]
    }
  ],
  top_mcap: [{ key: "count", label: "Count", inputType: "int", defaultValue: "200" }],
  top_volume: [{ key: "count", label: "Count", inputType: "int", defaultValue: "200" }],
  sector: [{ key: "sector", label: "Sector" }],
  industry: [{ key: "industry", label: "Industry" }],
  country: [{ key: "country", label: "Country" }],
  exchange: [{ key: "exchange", label: "Exchange" }],
  mcap_min: [{ key: "min", label: "Min", inputType: "number", defaultValue: "300000000" }],
  liquidity_min: [{ key: "min", label: "Min", inputType: "number", defaultValue: "1000000" }],
  price_min: [{ key: "min", label: "Min", inputType: "number", defaultValue: "5" }],
  price_max: [{ key: "max", label: "Max", inputType: "number", defaultValue: "500" }],
  cfo_growth: [{ key: "threshold", label: ">" }],
  fcf_growth: [{ key: "threshold", label: ">" }],
  rev_growth: [{ key: "threshold", label: ">" }],
  eps_growth: [{ key: "threshold", label: ">" }],
  dividend_yield: [{ key: "threshold", label: ">" }],
  ebitda_growth: [{ key: "threshold", label: ">" }],
  revenue_growth_yoy_over: [{ key: "threshold", label: ">", defaultValue: "15" }],
  net_income_growth_yoy_over: [{ key: "threshold", label: ">", defaultValue: "15" }],
  fcf_growth_yoy_over: [{ key: "threshold", label: ">", defaultValue: "15" }],
  pe_ttm_under: [{ key: "threshold", label: "<", defaultValue: "30" }],
  ps_ttm_under: [{ key: "threshold", label: "<", defaultValue: "10" }],
  gross_margin_over: [{ key: "threshold", label: ">", defaultValue: "30" }],
  fcf_margin_over: [{ key: "threshold", label: ">" }],
  debt_to_equity_under: [{ key: "threshold", label: "<", defaultValue: "1" }],
  interest_coverage_over: [{ key: "threshold", label: ">" }],
  pe_under: [{ key: "threshold", label: "<" }],
  pb_under: [{ key: "threshold", label: "<", defaultValue: "5" }],
  p_fcf_ttm_under: [{ key: "threshold", label: "<", defaultValue: "30" }],
  fcf_yield_over: [{ key: "threshold", label: ">", defaultValue: "5" }],
  roe_over: [{ key: "threshold", label: ">", defaultValue: "15" }],
  roa_over: [{ key: "threshold", label: ">", defaultValue: "5" }],
  operating_margin_over: [{ key: "threshold", label: ">", defaultValue: "10" }],
  net_margin_over: [{ key: "threshold", label: ">", defaultValue: "5" }],
  market_cap_over: [{ key: "threshold", label: ">", defaultValue: "300000000" }],
  enterprise_value_over: [{ key: "threshold", label: ">", defaultValue: "300000000" }],
  roic_over: [{ key: "threshold", label: ">" }],
  input_number: [{ key: "value", label: "Value" }],
  input_text: [{ key: "value", label: "Value" }]
};

const BLOCK_CODE = {
  setting_max_positions: (block) =>
    `settings.maxPositions = ${formatFieldExpression(block, "value")};`,
  setting_initial_capital: (block) =>
    `settings.initialCapital = ${formatFieldExpression(block, "value")};`,
  setting_min_cash_percent: (block) =>
    `settings.minCashPercent = ${formatFieldExpression(block, "value")};`,
  setting_max_position_size_percent: (block) =>
    `settings.maxPositionSizePercent = ${formatFieldExpression(block, "value")};`,
  setting_rebalance_days: (block) =>
    `settings.rebalanceDays = ${formatFieldExpression(block, "value")};`,
  setting_transaction_fee: (block) =>
    `settings.transactionFee = ${formatFieldExpression(block, "value")};`,
  setting_target_positions: (block) =>
    `settings.targetPositions = ${formatFieldExpression(block, "value")};`,
  setting_allocation: (block) =>
    `settings.allocation = ${formatFieldExpression(block, "value")};`,
  setting_min_holding_days: (block) =>
    `settings.minimumHoldingDays = ${formatFieldExpression(block, "value")};`,
  top_mcap: (block) =>
    `universe = topByMarketCap(universe, ${formatFieldExpression(block, "count")});`,
  top_volume: (block) =>
    `universe = topByVolume(universe, ${formatFieldExpression(block, "count")});`,
  filter: () => "universe = universe.filter(/* condition */);",
  sector: (block) =>
    `universe = universe.filterBySector(${formatFieldExpression(block, "sector")});`,
  industry: (block) =>
    `universe = universe.filterByIndustry(${formatFieldExpression(block, "industry")});`,
  country: (block) =>
    `universe = universe.filterByCountry(${formatFieldExpression(block, "country")});`,
  exchange: (block) =>
    `universe = universe.filterByExchange(${formatFieldExpression(block, "exchange")});`,
  mcap_min: (block) =>
    `universe = universe.filterByMarketCap(${formatFieldExpression(block, "min")});`,
  liquidity_min: (block) =>
    `universe = universe.filterByVolume(${formatFieldExpression(block, "min")});`,
  price_min: (block) =>
    `universe = universe.filterByPriceMin(${formatFieldExpression(block, "min")});`,
  price_max: (block) =>
    `universe = universe.filterByPriceMax(${formatFieldExpression(block, "max")});`,
  cfo_growth: (block) =>
    `signals.cfoGrowth = data.cfoGrowth > ${formatFieldExpression(block, "threshold")};`,
  fcf_growth: (block) =>
    `signals.fcfGrowth = data.fcfGrowth > ${formatFieldExpression(block, "threshold")};`,
  rev_growth: (block) =>
    `signals.revenueGrowth = data.revenueGrowth > ${formatFieldExpression(block, "threshold")};`,
  eps_growth: (block) =>
    `signals.epsGrowth = data.epsGrowth > ${formatFieldExpression(block, "threshold")};`,
  margin_up: () => "signals.marginsImproving = data.marginsTrend === 'up';",
  price_above_sma: () => "signals.priceAboveSma = data.price > indicators.sma50;",
  rsi_overbought: () => "signals.rsiOverbought = indicators.rsi > 70;",
  rsi_oversold: () => "signals.rsiOversold = indicators.rsi < 30;",
  momentum_6m: () => "signals.momentum6m = indicators.momentum6m > 0;",
  dividend_yield: (block) =>
    `signals.dividendYield = data.dividendYield > ${formatFieldExpression(block, "threshold")};`,
  macd_bullish: () => "signals.macdBullish = indicators.macdHist > 0;",
  ebitda_growth: (block) =>
    `signals.ebitdaGrowth = data.ebitdaGrowth > ${formatFieldExpression(block, "threshold")};`,
  positive_fcf: () => "signals.positiveFcf = fundamentals.free_cash_flow_ttm > 0;",
  positive_net_income: () => "signals.positiveNetIncome = fundamentals.net_income_ttm > 0;",
  revenue_growth_yoy_over: (block) =>
    `signals.revenueGrowthYoy = fundamentals.revenue_growth_yoy > ${formatFieldExpression(block, "threshold")};`,
  net_income_growth_yoy_over: (block) =>
    `signals.netIncomeGrowthYoy = fundamentals.net_income_growth_yoy > ${formatFieldExpression(block, "threshold")};`,
  fcf_growth_yoy_over: (block) =>
    `signals.fcfGrowthYoy = fundamentals.fcf_growth_yoy > ${formatFieldExpression(block, "threshold")};`,
  pe_ttm_under: (block) =>
    `signals.peTtm = fundamentals.pe_ttm < ${formatFieldExpression(block, "threshold")};`,
  ps_ttm_under: (block) =>
    `signals.psTtm = fundamentals.ps_ttm < ${formatFieldExpression(block, "threshold")};`,
  gross_margin_over: (block) =>
    `signals.grossMargin = fundamentals.gross_margin > ${formatFieldExpression(block, "threshold")};`,
  fcf_margin_over: (block) =>
    `signals.fcfMargin = data.fcfMargin > ${formatFieldExpression(block, "threshold")};`,
  debt_to_equity_under: (block) =>
    `signals.debtToEquity = fundamentals.debt_to_equity < ${formatFieldExpression(block, "threshold")};`,
  interest_coverage_over: (block) =>
    `signals.interestCoverage = data.interestCoverage > ${formatFieldExpression(block, "threshold")};`,
  pe_under: (block) => `signals.pe = fundamentals.pe_ttm < ${formatFieldExpression(block, "threshold")};`,
  pb_under: (block) => `signals.pb = fundamentals.pb < ${formatFieldExpression(block, "threshold")};`,
  p_fcf_ttm_under: (block) =>
    `signals.pFcfTtm = fundamentals.p_fcf_ttm < ${formatFieldExpression(block, "threshold")};`,
  fcf_yield_over: (block) =>
    `signals.fcfYield = fundamentals.fcf_yield > ${formatFieldExpression(block, "threshold")};`,
  roe_over: (block) => `signals.roe = fundamentals.roe > ${formatFieldExpression(block, "threshold")};`,
  roa_over: (block) => `signals.roa = fundamentals.roa > ${formatFieldExpression(block, "threshold")};`,
  operating_margin_over: (block) =>
    `signals.operatingMargin = fundamentals.operating_margin > ${formatFieldExpression(block, "threshold")};`,
  net_margin_over: (block) =>
    `signals.netMargin = fundamentals.net_margin > ${formatFieldExpression(block, "threshold")};`,
  market_cap_over: (block) =>
    `signals.marketCap = fundamentals.market_cap > ${formatFieldExpression(block, "threshold")};`,
  enterprise_value_over: (block) =>
    `signals.enterpriseValue = fundamentals.enterprise_value > ${formatFieldExpression(block, "threshold")};`,
  roic_over: (block) => `signals.roic = data.roic > ${formatFieldExpression(block, "threshold")};`,
  logic_and: () => "&&",
  logic_or: () => "||",
  ranking_variable: (block) => `ranking.variable = ${formatFieldExpression(block, "value")};`,
  ranking_direction: (block) => `ranking.direction = ${formatFieldExpression(block, "value")};`,
  input_number: (block) => formatFieldExpression(block, "value"),
  input_text: (block) => formatFieldExpression(block, "value")
};

const ACCEPTS = {
  universe: [
    "top_mcap",
    "top_volume",
    "mcap_min",
    "liquidity_min",
    "price_min",
    "price_max"
  ],
  signals: [
    "op_gt",
    "op_gte",
    "op_lt",
    "op_lte",
    "op_eq",
    "op_neq",
    "op_and",
    "op_or",
    "logic_and",
    "logic_or"
  ],
  execution: [],
  exit: [
    "op_gt",
    "op_gte",
    "op_lt",
    "op_lte",
    "op_eq",
    "op_neq",
    "op_and",
    "op_or",
    "logic_and",
    "logic_or"
  ]
};

const defaultEdgeOptions = {
  style: { strokeWidth: 2, stroke: "#666" }
};

const makeId = () =>
  crypto.randomUUID ? crypto.randomUUID() : `id_${Math.random().toString(16).slice(2)}`;

const SETTING_BLOCK_TYPES = [
  "setting_max_positions",
  "setting_initial_capital",
  "setting_min_cash_percent",
  "setting_max_position_size_percent",
  "setting_rebalance_days",
  "setting_transaction_fee",
  "setting_target_positions",
  "setting_allocation",
  "setting_min_holding_days"
];

// richer block objects (operators have 2 args)
const makeBlock = (type) => {
  const def = BLOCKS.find((b) => b.type === type);
  const label = def?.label ?? type;

  if (isOperator(type)) {
    return {
      id: makeId(),
      type,
      label,
      kind: "operator",
      args: [
        { mode: "literal", value: "" }, // left
        { mode: "literal", value: "" } // right
      ]
    };
  }

  if (isLogicBlock(type)) {
    return { id: makeId(), type, label, kind: "logic" };
  }

  if (isVariable(type)) {
    return { id: makeId(), type, label, kind: "variable" };
  }

  if (isInput(type)) {
    return {
      id: makeId(),
      type,
      label,
      kind: "input",
      fields: { value: { mode: "literal", value: "" } }
    };
  }

  const fieldDefs = BLOCK_FIELDS[type] ?? [];
  const fields = fieldDefs.reduce((acc, field) => {
    acc[field.key] = {
      mode: "literal",
      value: field.defaultValue ?? (field.inputType === "bool" ? "false" : "")
    };
    return acc;
  }, {});

  return { id: makeId(), type, label, kind: "block", fields };
};

const makeFixedSettingBlock = (type) => ({
  ...makeBlock(type),
  id: type,
  fixed: true
});

const makeDefaultSettingBlocks = () => SETTING_BLOCK_TYPES.map(makeFixedSettingBlock);
const RANKING_BLOCK_TYPES = ["ranking_variable", "ranking_direction"];
const makeDefaultRankingBlocks = () =>
  RANKING_BLOCK_TYPES.map((type) => ({
    ...makeBlock(type),
    id: type,
    fixed: true
  }));

const normalizeRankingBlocks = (blocks = []) => {
  const currentVariable = blocks.find((block) => block.type === "ranking_variable");
  const currentDirection = blocks.find((block) => block.type === "ranking_direction");
  const legacyRanking = blocks.find((block) => block.type === "ranking");
  const defaults = makeDefaultRankingBlocks();

  const variableBlock = {
    ...defaults[0],
    ...(currentVariable ?? {}),
    fields: {
      value:
        currentVariable?.fields?.value ??
        legacyRanking?.fields?.variable ??
        defaults[0].fields.value
    }
  };

  const directionBlock = {
    ...defaults[1],
    ...(currentDirection ?? {}),
    fields: {
      value:
        currentDirection?.fields?.value ??
        legacyRanking?.fields?.direction ??
        defaults[1].fields.value
    }
  };

  return [variableBlock, directionBlock];
};

const normalizeLiteral = (value) => {
  if (value === undefined || value === null || value === "") return "undefined";
  if (typeof value === "number") return String(value);
  const trimmed = String(value).trim();
  if (!trimmed) return "undefined";
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
  if (trimmed === "true" || trimmed === "false") return trimmed;
  return JSON.stringify(trimmed);
};

const stopNodeDeletionKey = (event) => {
  if (event.key === "Backspace" || event.key === "Delete") {
    event.stopPropagation();
  }
};

const keepInputFocus = (event) => {
  event.stopPropagation();
};

const formatInlineExpression = (value) => {
  if (!value) return "undefined";
  return value;
};

const formatArgExpression = (arg, percentage = false) => {
  if (!arg) return "undefined";
  if (arg.mode === "var") {
    return VARIABLE_TO_CODE[arg.varType] ?? VARIABLE_IDENTIFIERS[arg.varType] ?? arg.label ?? "unknown";
  }
  if (arg.mode === "expr") {
    return formatInlineExpression(arg.value);
  }
  if (percentage) {
    const number = Number(arg.value);
    if (arg.value !== "" && Number.isFinite(number)) return String(number / 100);
  }
  return normalizeLiteral(arg.value);
};

const formatFieldExpression = (block, key) => {
  const field = block?.fields?.[key];
  return formatArgExpression(field, key === "threshold" && PERCENT_THRESHOLD_BLOCK_TYPES.has(block.type));
};

const formatOperatorExpression = (block) => {
  const symbol = OPERATOR_SYMBOLS[block.type] ?? block.label;
  const leftArg = block.args?.[0];
  const rightArg = block.args?.[1];
  const left = formatArgExpression(leftArg, rightArg?.mode === "var" && PERCENT_VARIABLE_TYPES.has(rightArg.varType));
  const right = formatArgExpression(rightArg, leftArg?.mode === "var" && PERCENT_VARIABLE_TYPES.has(leftArg.varType));
  return `(${left} ${symbol} ${right})`;
};

const buildSignalExpressionForBlock = (block) => {
  if (block.kind === "operator") {
    return formatOperatorExpression(block);
  }

  switch (block.type) {
    case "cfo_growth":
      return `(data.cfoGrowth > ${formatFieldExpression(block, "threshold")})`;
    case "fcf_growth":
      return `(data.fcfGrowth > ${formatFieldExpression(block, "threshold")})`;
    case "rev_growth":
      return `(data.revenueGrowth > ${formatFieldExpression(block, "threshold")})`;
    case "eps_growth":
      return `(data.epsGrowth > ${formatFieldExpression(block, "threshold")})`;
    case "margin_up":
      return "(data.marginsTrend === 'up')";
    case "price_above_sma":
      return "(data.price > indicators.sma50)";
    case "rsi_overbought":
      return "(indicators.rsi > 70)";
    case "rsi_oversold":
      return "(indicators.rsi < 30)";
    case "momentum_6m":
      return "(indicators.momentum6m > 0)";
    case "dividend_yield":
      return `(data.dividendYield > ${formatFieldExpression(block, "threshold")})`;
    case "macd_bullish":
      return "(indicators.macdHist > 0)";
    case "ebitda_growth":
      return `(data.ebitdaGrowth > ${formatFieldExpression(block, "threshold")})`;
    case "positive_fcf":
      return "(fundamentals.free_cash_flow_ttm > 0)";
    case "positive_net_income":
      return "(fundamentals.net_income_ttm > 0)";
    case "revenue_growth_yoy_over":
      return `(fundamentals.revenue_growth_yoy > ${formatFieldExpression(block, "threshold")})`;
    case "net_income_growth_yoy_over":
      return `(fundamentals.net_income_growth_yoy > ${formatFieldExpression(block, "threshold")})`;
    case "fcf_growth_yoy_over":
      return `(fundamentals.fcf_growth_yoy > ${formatFieldExpression(block, "threshold")})`;
    case "pe_ttm_under":
      return `(fundamentals.pe_ttm < ${formatFieldExpression(block, "threshold")})`;
    case "ps_ttm_under":
      return `(fundamentals.ps_ttm < ${formatFieldExpression(block, "threshold")})`;
    case "gross_margin_over":
      return `(fundamentals.gross_margin > ${formatFieldExpression(block, "threshold")})`;
    case "fcf_margin_over":
      return `(data.fcfMargin > ${formatFieldExpression(block, "threshold")})`;
    case "debt_to_equity_under":
      return `(fundamentals.debt_to_equity < ${formatFieldExpression(block, "threshold")})`;
    case "interest_coverage_over":
      return `(data.interestCoverage > ${formatFieldExpression(block, "threshold")})`;
    case "pe_under":
      return `(fundamentals.pe_ttm < ${formatFieldExpression(block, "threshold")})`;
    case "pb_under":
      return `(fundamentals.pb < ${formatFieldExpression(block, "threshold")})`;
    case "p_fcf_ttm_under":
      return `(fundamentals.p_fcf_ttm < ${formatFieldExpression(block, "threshold")})`;
    case "fcf_yield_over":
      return `(fundamentals.fcf_yield > ${formatFieldExpression(block, "threshold")})`;
    case "roe_over":
      return `(fundamentals.roe > ${formatFieldExpression(block, "threshold")})`;
    case "roa_over":
      return `(fundamentals.roa > ${formatFieldExpression(block, "threshold")})`;
    case "operating_margin_over":
      return `(fundamentals.operating_margin > ${formatFieldExpression(block, "threshold")})`;
    case "net_margin_over":
      return `(fundamentals.net_margin > ${formatFieldExpression(block, "threshold")})`;
    case "market_cap_over":
      return `(fundamentals.market_cap > ${formatFieldExpression(block, "threshold")})`;
    case "enterprise_value_over":
      return `(fundamentals.enterprise_value > ${formatFieldExpression(block, "threshold")})`;
    case "roic_over":
      return `(data.roic > ${formatFieldExpression(block, "threshold")})`;
    default:
      return null;
  }
};

const buildSignalCondition = (blocks) => {
  let condition = "";
  let pendingOp = null;

  blocks.forEach((block) => {
    if (block.kind === "logic") {
      pendingOp = LOGIC_SYMBOLS[block.type] ?? null;
      return;
    }

    const expression = buildSignalExpressionForBlock(block);
    if (!expression) return;

    if (!condition) {
      condition = expression;
      pendingOp = null;
      return;
    }

    const operator = pendingOp ?? "&&";
    condition = `(${condition} ${operator} ${expression})`;
    pendingOp = null;
  });

  return condition || null;
};

const buildCodeForBlocks = (blocks) => {
  if (!blocks.length) return ["// Drag blocks here to generate code."];

  return blocks.flatMap((block) => {
    if (block.kind === "logic") {
      return [];
    }

    if (block.kind === "operator") {
      const opId = `op_${block.id.replace(/[^a-zA-Z0-9]/g, "").slice(-6)}`;
      return [`const ${opId} = ${formatOperatorExpression(block)};`];
    }

    if (block.kind === "variable") {
      const name = VARIABLE_IDENTIFIERS[block.type] ?? block.type.replace("var_", "");
      const value = VARIABLE_TO_CODE[block.type] ?? block.label ?? "undefined";
      return [`const ${name} = ${value};`];
    }

    if (block.kind === "input") {
      const suffix = block.id.replace(/[^a-zA-Z0-9]/g, "").slice(-6);
      const name = `${block.type.replace("input_", "input")}_${suffix}`;
      return [`const ${name} = ${formatFieldExpression(block, "value")};`];
    }

    const template = BLOCK_CODE[block.type];
    if (template) {
      return [template(block)];
    }

    return [`// ${block.label}`];
  });
};

const parseConfigValue = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "number" || typeof value === "boolean") return value;
  const trimmed = String(value).trim();
  if (!trimmed) return undefined;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  return trimmed;
};

const getSimpleFieldValue = (block, key) => {
  const field = block?.fields?.[key];
  if (!field) return undefined;

  if (field.mode === "var") {
    return VARIABLE_TO_CODE[field.varType] ?? VARIABLE_IDENTIFIERS[field.varType] ?? field.label;
  }

  if (field.mode === "expr") {
    return field.value || undefined;
  }

  return parseConfigValue(field.value);
};

const sortNodesByPosition = (nodes) =>
  nodes
    .slice()
    .sort((a, b) => (a.position?.x ?? 0) - (b.position?.x ?? 0) || (a.position?.y ?? 0) - (b.position?.y ?? 0));

const sortNodeIdsByPosition = (nodeIds, nodeMap) =>
  nodeIds
    .slice()
    .sort((leftId, rightId) => {
      const left = nodeMap.get(leftId);
      const right = nodeMap.get(rightId);
      return (left?.position?.x ?? 0) - (right?.position?.x ?? 0) || (left?.position?.y ?? 0) - (right?.position?.y ?? 0);
    });

const buildAdjacencyMap = (nodes, edges) => {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const adjacency = edges.reduce((acc, edge) => {
    acc[edge.source] ||= [];
    acc[edge.source].push(edge.target);
    return acc;
  }, {});

  Object.keys(adjacency).forEach((sourceId) => {
    adjacency[sourceId] = sortNodeIdsByPosition(adjacency[sourceId], nodeMap);
  });

  return { nodeMap, adjacency };
};

const buildFlows = (nodes, edges) => {
  const { nodeMap, adjacency } = buildAdjacencyMap(nodes, edges);

  const settingNodes = sortNodesByPosition(nodes.filter((node) => node.type === "setting"));
  const universeNodes = sortNodesByPosition(nodes.filter((node) => node.type === "universe"));
  const startNodes = settingNodes.length ? settingNodes : universeNodes;
  const visited = new Set();
  const flows = [];

  const traverseFrom = (startId) => {
    const queue = [startId];
    const ordered = [];

    while (queue.length) {
      const currentId = queue.shift();
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const node = nodeMap.get(currentId);
      if (node) ordered.push(node);
      (adjacency[currentId] || []).forEach((nextId) => queue.push(nextId));
    }

    return sortNodesByPosition(ordered);
  };

  startNodes.forEach((node) => {
    const flow = traverseFrom(node.id);
    if (flow.length) flows.push(flow);
  });

  const remaining = sortNodesByPosition(nodes.filter((node) => !visited.has(node.id)));
  if (remaining.length) {
    const remainingSet = new Set(remaining.map((node) => node.id));

    remaining.forEach((node) => {
      if (!remainingSet.has(node.id)) return;
      const component = traverseFrom(node.id).filter((entry) => remainingSet.has(entry.id));
      component.forEach((entry) => remainingSet.delete(entry.id));
      if (component.length) flows.push(sortNodesByPosition(component));
    });
  }

  if (!flows.length) {
    return [sortNodesByPosition(nodes)];
  }

  return flows;
};

const buildFlowPaths = (flowNodes, adjacency) => {
  const flowNodeMap = new Map(flowNodes.map((node) => [node.id, node]));
  const flowNodeIds = new Set(flowNodes.map((node) => node.id));
  const inDegree = {};

  flowNodes.forEach((node) => {
    inDegree[node.id] = 0;
  });

  flowNodes.forEach((node) => {
    (adjacency[node.id] || []).forEach((nextId) => {
      if (flowNodeIds.has(nextId)) {
        inDegree[nextId] = (inDegree[nextId] || 0) + 1;
      }
    });
  });

  const settingStarts = sortNodesByPosition(flowNodes.filter((node) => node.type === "setting"));
  const universeStarts = sortNodesByPosition(flowNodes.filter((node) => node.type === "universe"));
  const fallbackStarts = sortNodesByPosition(flowNodes.filter((node) => (inDegree[node.id] || 0) === 0));
  const startNodes = settingStarts.length
    ? settingStarts
    : universeStarts.length
      ? universeStarts
      : fallbackStarts.length
        ? fallbackStarts
        : sortNodesByPosition(flowNodes).slice(0, 1);
  const paths = [];

  const walk = (nodeId, currentPath, seen) => {
    if (seen.has(nodeId)) {
      paths.push(currentPath);
      return;
    }

    const nextPath = [...currentPath, nodeId];
    const nextSeen = new Set(seen);
    nextSeen.add(nodeId);

    const nextIds = (adjacency[nodeId] || []).filter((nextId) => flowNodeIds.has(nextId));
    if (!nextIds.length) {
      paths.push(nextPath);
      return;
    }

    nextIds.forEach((nextId) => walk(nextId, nextPath, nextSeen));
  };

  startNodes.forEach((node) => walk(node.id, [], new Set()));

  const uniquePaths = new Map();
  paths.forEach((pathNodeIds) => {
    const key = pathNodeIds.join("->");
    if (!uniquePaths.has(key)) {
      uniquePaths.set(key, pathNodeIds);
    }
  });

  return Array.from(uniquePaths.values()).map((pathNodeIds) =>
    pathNodeIds.map((nodeId) => flowNodeMap.get(nodeId)).filter(Boolean)
  );
};

const buildPathCode = (conditionExpression, rankingLines) => [
  `if (${conditionExpression || "true"}) {`,
  ...(rankingLines.length ? rankingLines : ["// Configure ranking for this path."]).map((line) => `  ${line}`),
  "}"
];

const SETTING_OUTPUT_KEYS = {
  setting_max_positions: ["max_positions", "value"],
  setting_initial_capital: ["initial_capital", "value"],
  setting_min_cash_percent: ["min_cash_percent", "value"],
  setting_max_position_size_percent: ["max_position_size_percent", "value"],
  setting_rebalance_days: ["rebalance_days", "value"],
  setting_transaction_fee: ["transaction_fee", "value"],
  setting_target_positions: ["target_positions", "value"],
  setting_allocation: ["allocation", "value"],
  setting_min_holding_days: ["minimum_holding_days", "value"]
};

const UNIVERSE_OUTPUT_KEYS = {
  top_mcap: ["top_n_by_market_cap", "count"],
  top_volume: ["top_n_by_volume", "count"],
  mcap_min: ["market_cap_min", "min"],
  liquidity_min: ["volume_min", "min"],
  price_min: ["price_min", "min"],
  price_max: ["price_max", "max"]
};

const CONDITION_OUTPUT_KEYS = {
  rsi_overbought: "rsi_overbought",
  rsi_oversold: "rsi_oversold",
  macd_bullish: "macd_bullish",
  positive_fcf: "positive_fcf",
  positive_net_income: "positive_net_income",
  revenue_growth_yoy_over: "revenue_growth_yoy_over",
  net_income_growth_yoy_over: "net_income_growth_yoy_over",
  fcf_growth_yoy_over: "fcf_growth_yoy_over",
  pe_ttm_under: "pe_ttm_under",
  ps_ttm_under: "ps_ttm_under",
  pb_under: "pb_under",
  p_fcf_ttm_under: "p_fcf_ttm_under",
  fcf_yield_over: "fcf_yield_over",
  roe_over: "roe_over",
  roa_over: "roa_over",
  gross_margin_over: "gross_margin_over",
  operating_margin_over: "operating_margin_over",
  net_margin_over: "net_margin_over",
  debt_to_equity_under: "debt_to_equity_under",
  market_cap_over: "market_cap_over",
  enterprise_value_over: "enterprise_value_over"
};

const assignSimpleValue = (target, key, value) => {
  if (Object.prototype.hasOwnProperty.call(target, key)) {
    target[key] = Array.isArray(target[key]) ? [...target[key], value] : [target[key], value];
    return;
  }

  target[key] = value;
};

const buildSimpleSection = (nodes, keyMap) =>
  sortNodesByPosition(nodes).reduce((acc, node) => {
    (node.data?.blocks ?? []).forEach((block) => {
      const mapping = keyMap[block.type];
      if (!mapping) return;
      const [outputKey, fieldKey] = mapping;
      assignSimpleValue(acc, outputKey, fieldKey === null ? true : getSimpleFieldValue(block, fieldKey));
    });
    return acc;
  }, {});

const buildConditionOutputKey = (block) => {
  if (block.kind === "operator") {
    return formatOperatorExpression(block);
  }

  return CONDITION_OUTPUT_KEYS[block.type] ?? block.type;
};

const buildSimpleConditions = (conditionNodes) =>
  sortNodesByPosition(conditionNodes)
    .flatMap((node) => node.data?.blocks ?? [])
    .filter((block) => !isLogicBlock(block.type))
    .map(buildConditionOutputKey);

const buildSimpleRanking = (rankingNodes) => {
  const rankingBlocks = sortNodesByPosition(rankingNodes).flatMap((node) =>
    normalizeRankingBlocks(node.data?.blocks ?? [])
  );
  const variableBlock = rankingBlocks.find((block) => block.type === "ranking_variable");
  const directionBlock = rankingBlocks.find((block) => block.type === "ranking_direction");

  if (!variableBlock && !directionBlock) return {};

  return {
    variable: variableBlock ? getSimpleFieldValue(variableBlock, "value") : undefined,
    direction: directionBlock ? getSimpleFieldValue(directionBlock, "value") : undefined
  };
};

const buildStrategyPaths = (nodes, edges) => {
  const paths = buildFlows(nodes, edges).flatMap((flowNodes) => {
    const { adjacency } = buildAdjacencyMap(flowNodes, edges);
    return buildFlowPaths(flowNodes, adjacency);
  });

  const branchPaths = paths
    .map((pathNodes) =>
      sortNodesByPosition(pathNodes.filter((node) => node.type === "signals" || node.type === "execution"))
    )
    .filter((pathNodes) => pathNodes.length > 0);

  const uniquePaths = new Map();
  branchPaths.forEach((pathNodes) => {
    const key = pathNodes.map((node) => node.id).join("->");
    if (!uniquePaths.has(key)) uniquePaths.set(key, pathNodes);
  });

  return Array.from(uniquePaths.values()).map((pathNodes, pathIndex) => {
    const conditionNodes = sortNodesByPosition(pathNodes.filter((node) => node.type === "signals"));
    const rankingNodes = sortNodesByPosition(pathNodes.filter((node) => node.type === "execution"));
    const conditionBlocks = conditionNodes.flatMap((node) => node.data?.blocks ?? []);
    const conditionExpression = buildSignalCondition(conditionBlocks);
    const rankingLines = rankingNodes.flatMap((node) => buildCodeForBlocks(node.data?.blocks ?? []));

    return {
      id: `path_${pathIndex + 1}`,
      conditions: buildSimpleConditions(conditionNodes),
      condition: conditionExpression,
      ranking: buildSimpleRanking(rankingNodes),
      code: buildPathCode(conditionExpression, rankingLines)
    };
  });
};

const buildStrategyDefinition = (nodes, edges, settingBlocks = []) => {
  const universeNodes = sortNodesByPosition(nodes.filter((node) => node.type === "universe"));
  const exitNodes = sortNodesByPosition(nodes.filter((node) => node.type === "exit"));
  const buyNode = nodes.find((node) => node.type === "signals");
  const exitNode = exitNodes[0];
  const rankingNode = nodes.find((node) => node.type === "execution");

  return {
    version: 1,
    settings: buildSimpleSection([{ data: { blocks: settingBlocks } }], SETTING_OUTPUT_KEYS),
    universe: buildSimpleSection(universeNodes, UNIVERSE_OUTPUT_KEYS),
    paths: buildStrategyPaths(nodes, edges),
    ranking: buildSimpleRanking(rankingNode ? [rankingNode] : []),
    buy_match: buyNode?.data?.matchMode ?? "all",
    exit: {
      match: exitNode?.data?.matchMode ?? "any",
      conditions: buildSimpleConditions(exitNodes)
    }
  };
};

const humanizeStrategyKey = (value) => String(value ?? "")
  .replace(/[_.]+/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const STRATEGY_METRIC_LABELS = {
  "fundamentals.revenue_growth_yoy": "year-over-year revenue growth",
  "fundamentals.net_income_growth_yoy": "year-over-year net income growth",
  "fundamentals.fcf_growth_yoy": "year-over-year free cash flow growth",
  "fundamentals.pb": "price-to-book ratio",
  "fundamentals.pe_ttm": "price-to-earnings ratio",
  "fundamentals.ps_ttm": "price-to-sales ratio",
  "fundamentals.gross_margin": "gross margin",
  "fundamentals.operating_margin": "operating margin",
  "fundamentals.net_margin": "net margin",
  "fundamentals.roe": "return on equity",
  "fundamentals.roa": "return on assets",
  "fundamentals.debt_to_equity": "debt-to-equity ratio",
  "fundamentals.market_cap": "market capitalization",
  "fundamentals.enterprise_value": "enterprise value",
  "fundamentals.fcf_yield": "free cash flow yield",
  "fundamentals.p_fcf_ttm": "price-to-free-cash-flow ratio"
};

const isPercentMetric = (metric) => /(growth|margin|yield|\.roe$|\.roa$)/.test(metric);
const formatStrategyNumber = (value, percent = false) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  const displayed = percent ? number * 100 : number;
  return `${Number(displayed.toFixed(2))}${percent ? "%" : ""}`;
};
const joinNaturally = (items, conjunction = "and") => {
  if (items.length < 2) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, ${conjunction} ${items[items.length - 1]}`;
};
const describeCondition = (condition) => {
  const operatorWords = { ">": "is above", "<": "is below", ">=": "is at least", "<=": "is at most", "==": "equals", "===": "equals" };
  const text = String(condition ?? "").replace(/[()]/g, "").trim();
  if (STRATEGY_METRIC_LABELS[text]) return STRATEGY_METRIC_LABELS[text];
  const described = text.replace(/([a-zA-Z_][\w.]*)\s*(>=|<=|>|<|===|==)\s*(-?\d+(?:\.\d+)?)/g, (_, metric, operator, value) => {
    const label = STRATEGY_METRIC_LABELS[metric] ?? humanizeStrategyKey(metric.replace(/^(data|indicators|fundamentals)\./, "")).toLowerCase();
    return `${label} ${operatorWords[operator]} ${formatStrategyNumber(value, isPercentMetric(metric))}`;
  });
  return described
    .replace(/\s*&&\s*/g, " and ")
    .replace(/\s*\|\|\s*/g, " or ")
    .replace(/_/g, " ");
};

const describeUniverseNaturally = (universe) => {
  const parts = [];
  if (universe?.top_n_by_market_cap) parts.push(`the ${universe.top_n_by_market_cap} largest companies by market capitalization`);
  if (universe?.top_n_by_volume) parts.push(`the ${universe.top_n_by_volume} most actively traded stocks`);
  if (universe?.market_cap_min) parts.push(`companies worth at least ${formatCurrency(Number(universe.market_cap_min))}`);
  if (universe?.volume_min) parts.push(`stocks trading at least ${Number(universe.volume_min).toLocaleString()} shares`);
  if (universe?.price_min) parts.push(`stocks priced at or above $${universe.price_min}`);
  if (universe?.price_max) parts.push(`stocks priced at or below $${universe.price_max}`);
  return parts.length ? `The strategy starts with ${joinNaturally(parts)}.` : "The strategy does not apply any additional universe filters.";
};

const describePortfolioNaturally = (settings) => {
  const target = settings?.target_positions ?? settings?.max_positions;
  const allocation = String(settings?.allocation ?? "").replace(/_/g, " ");
  const parts = [];
  if (target) parts.push(`targets ${target} ${allocation || "equally sized"} positions`);
  if (settings?.initial_capital !== undefined) parts.push(`starts with ${formatCurrency(Number(settings.initial_capital))}`);
  if (settings?.min_cash_percent) parts.push(`keeps at least ${settings.min_cash_percent}% in cash`);
  if (settings?.max_position_size_percent) parts.push(`limits each position to ${settings.max_position_size_percent}% of the portfolio`);
  if (settings?.rebalance_days) parts.push(`rebalances every ${settings.rebalance_days} days`);
  if (Number(settings?.transaction_fee) > 0) parts.push(`assumes a ${settings.transaction_fee}% transaction fee`);
  if (Number(settings?.minimum_holding_days) > 0) parts.push(`holds positions for at least ${settings.minimum_holding_days} days`);
  return parts.length ? `It ${joinNaturally(parts)}.` : "It uses the default portfolio settings.";
};

const buildDeterministicStrategySummary = (strategy) => {
  const paths = Array.isArray(strategy?.paths) ? strategy.paths : [];
  const entryConditions = paths.flatMap((path) => path.conditions ?? []);
  const exitConditions = strategy?.exit?.conditions ?? [];
  const ranking = strategy?.ranking?.variable
    ? strategy.ranking
    : paths.find((path) => path.ranking?.variable)?.ranking ?? {};
  const universe = describeUniverseNaturally(strategy?.universe);
  const entry = entryConditions.length
    ? `It buys a stock when ${strategy?.buy_match === "any" ? "at least one" : "all"} of the following ${entryConditions.length === 1 ? "condition is" : "conditions are"} met: ${joinNaturally(entryConditions.map(describeCondition))}.`
    : "It does not currently have any entry conditions.";
  const rankingText = ranking.variable
    ? `Among qualifying stocks, it prioritizes those with the ${String(ranking.direction).toLowerCase().includes("low") || ranking.direction === "ascending" ? "lowest" : "highest"} ${describeCondition(ranking.variable)}.`
    : "It does not apply an additional ranking rule.";
  const exit = exitConditions.length
    ? `It exits a position when ${strategy?.exit?.match === "all" ? "all" : "any"} of the following ${exitConditions.length === 1 ? "condition is" : "conditions are"} met: ${joinNaturally(exitConditions.map(describeCondition), strategy?.exit?.match === "all" ? "and" : "or")}.`
    : "It does not currently have any exit conditions.";
  const portfolio = describePortfolioNaturally(strategy?.settings);

  return {
    version: 1,
    text: [universe, entry, rankingText, exit, portfolio].join(" "),
    sections: { universe, entry, ranking: rankingText, exit, portfolio }
  };
};


const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const hashString = (value) => {
  let hash = 2166136261;
  const text = typeof value === "string" ? value : JSON.stringify(value);

  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const makeSeededRandom = (seed) => {
  let state = seed || 1;

  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const randomNormal = (random) => {
  const u = Math.max(random(), 1e-9);
  const v = Math.max(random(), 1e-9);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};

const safeNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatCurrency = (value) => Number.isFinite(value) ? new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value) : "N/A";

const formatPercent = (value, digits = 1) => Number.isFinite(value) ? `${(value * 100).toFixed(digits)}%` : "N/A";

const formatMetricNumber = (value, digits = 2) =>
  Number.isFinite(value) ? value.toFixed(digits) : "N/A";

const generateRandomBacktest = (strategyDefinition) => {
  const settings = strategyDefinition?.settings ?? {};
  const paths = strategyDefinition?.paths ?? [];
  const firstPath = paths[0] ?? {};
  const initialCapital = safeNumber(settings.initial_capital, 10000);
  const targetPositions = clampNumber(safeNumber(settings.target_positions, 20), 1, 80);
  const rebalanceDays = clampNumber(safeNumber(settings.rebalance_days, 30), 1, 252);
  const fee = Math.max(0, safeNumber(settings.transaction_fee, 0));
  const conditionCount = paths.reduce((sum, path) => sum + (path.conditions?.length ?? 0), 0);
  const universeRuleCount = Object.keys(strategyDefinition?.universe ?? {}).length;
  const seed = hashString(strategyDefinition);
  const random = makeSeededRandom(seed);
  const tradingDays = 252 * 3;
  const yearlyEdge = 0.055 + conditionCount * 0.007 + universeRuleCount * 0.003;
  const diversificationPenalty = targetPositions < 8 ? (8 - targetPositions) * 0.018 : 0;
  const annualVolatility = clampNumber(0.13 + diversificationPenalty + Math.max(0, 20 - targetPositions) * 0.0025, 0.09, 0.38);
  const dailyMean = yearlyEdge / 252 - (fee / Math.max(1, rebalanceDays)) / 10000;
  const dailyVol = annualVolatility / Math.sqrt(252);
  const equity = [];
  const dailyReturns = [];
  let value = initialCapital;
  let peak = initialCapital;
  let maxDrawdown = 0;
  let winningDays = 0;
  let losingDays = 0;

  for (let day = 0; day <= tradingDays; day += 1) {
    if (day > 0) {
      const shock = randomNormal(random);
      const regimeNoise = Math.sin(day / 45) * dailyVol * 0.18;
      const crashChance = random() < 0.012 ? -random() * dailyVol * 4.5 : 0;
      const dailyReturn = dailyMean + shock * dailyVol + regimeNoise + crashChance;
      dailyReturns.push(dailyReturn);
      if (dailyReturn >= 0) winningDays += 1;
      else losingDays += 1;
      value *= 1 + dailyReturn;
      peak = Math.max(peak, value);
      maxDrawdown = Math.min(maxDrawdown, value / peak - 1);
    }

    if (day % 21 === 0 || day === tradingDays) {
      equity.push({
        label: day === 0 ? "Start" : `M${Math.round(day / 21)}`,
        day,
        value: Math.round(value)
      });
    }
  }

  const totalReturn = value / initialCapital - 1;
  const years = tradingDays / 252;
  const cagr = Math.pow(value / initialCapital, 1 / years) - 1;
  const averageDailyReturn = dailyReturns.reduce((sum, entry) => sum + entry, 0) / Math.max(1, dailyReturns.length);
  const dailyVariance =
    dailyReturns.reduce((sum, entry) => sum + Math.pow(entry - averageDailyReturn, 2), 0) /
    Math.max(1, dailyReturns.length - 1);
  const volatility = Math.sqrt(dailyVariance) * Math.sqrt(252);
  const sharpe = volatility ? (averageDailyReturn * 252 - 0.02) / volatility : 0;
  const rebalanceCount = Math.floor(tradingDays / rebalanceDays);
  const tradeCount = Math.round(rebalanceCount * targetPositions * (0.35 + random() * 0.3));

  return {
    generatedAt: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    strategySummary: {
      initialCapital,
      targetPositions,
      rebalanceDays,
      conditionCount,
      rankingVariable: firstPath.ranking?.variable ?? "Not set",
      rankingDirection: firstPath.ranking?.direction ?? "Not set"
    },
    metrics: {
      finalValue: Math.round(value),
      totalReturn,
      cagr,
      sharpe,
      maxDrawdown,
      volatility,
      winRate: winningDays / Math.max(1, winningDays + losingDays),
      rebalances: rebalanceCount,
      trades: tradeCount
    },
    equity
  };
};

/* =====================================================
   SIDEBAR
===================================================== */

const getPrettyDefaultValue = (blockType, fieldKey) => {
  const field = (BLOCK_FIELDS[blockType] ?? []).find((entry) => entry.key === fieldKey);
  const raw = field?.defaultValue ?? "";

  if (blockType === "top_mcap" || blockType === "top_volume") return raw || "200";
  if (blockType === "mcap_min") return "300M";
  if (blockType === "liquidity_min") return "1M";
  return raw;
};

const SCRATCH_BLOCK_LABELS = {
  top_mcap: ["Top", "by market cap"],
  top_volume: ["Top", "by volume"],
  mcap_min: ["Market cap >=", ""],
  liquidity_min: ["Volume >=", ""],
  price_min: ["Price >=", ""],
  price_max: ["Price <=", ""]
};

function PaletteOperatorPreview({ block }) {
  return (
    <div style={paletteOperatorPreviewStyle}>
      <span style={paletteArgSlotPreviewStyle} />
      <span style={paletteOperatorSymbolStyle}>{block.label}</span>
      <span style={paletteArgSlotPreviewStyle} />
    </div>
  );
}

function PaletteScratchPreview({ block }) {
  const fieldDef = (BLOCK_FIELDS[block.type] ?? [])[0];
  const labels = SCRATCH_BLOCK_LABELS[block.type];

  if (!labels || !fieldDef) {
    return <span style={paletteBlockLabelStyle}>{block.label}</span>;
  }

  return (
    <div style={paletteScratchRowStyle}>
      <span style={paletteBlockLabelStyle}>{labels[0]}</span>
      <span style={paletteScratchInputPillStyle}>
        {getPrettyDefaultValue(block.type, fieldDef.key)}
      </span>
      {labels[1] && <span style={paletteBlockLabelStyle}>{labels[1]}</span>}
    </div>
  );
}

function PaletteBlockPreview({ block, groupKey }) {
  if (isOperator(block.type)) return <PaletteOperatorPreview block={block} />;
  if (groupKey === "universe") return <PaletteScratchPreview block={block} />;
  return <span style={paletteBlockLabelStyle}>{block.label}</span>;
}

function BlockPaletteContent({ selectedSection }) {
  const grouped = useMemo(() => {
    const map = {};
    for (const b of BLOCKS) {
      map[b.group] ||= [];
      map[b.group].push(b);
    }
    return map;
  }, []);

  return (
    <div style={paletteContentStyle}>
      {Object.entries(PALETTE_GROUPS).map(([groupKey, meta]) => {
        const visibleGroups = selectedSection === "universe"
          ? ["universe"]
          : selectedSection === "signals" || selectedSection === "exit"
            ? ["fundamentals", "technicals", "operators"]
            : selectedSection === "execution"
              ? []
              : Object.keys(PALETTE_GROUPS);
        if (!visibleGroups.includes(groupKey)) return null;
        const items = grouped[groupKey] || [];
        if (items.length === 0) return null;

        return (
          <div key={groupKey} style={{ marginBottom: 18 }}>
            <div style={paletteHeader(meta.accent)}>{meta.label}</div>

            <div style={paletteGrid(groupKey)}>
              {items.map((b) => (
                <div
                  key={b.type}
                  draggable
                  onDragStart={(e) => {
                    // palette always sets block/type
                    e.dataTransfer.setData("block/type", b.type);

                    // if it's a variable, also provide a direct payload for arg slots
                    if (b.type.startsWith("var_")) {
                      e.dataTransfer.setData(
                        "mini/payload",
                        JSON.stringify({ kind: "variable", type: b.type, label: b.label })
                      );
                    }

                    if (b.type.startsWith("input_")) {
                      e.dataTransfer.setData(
                        "mini/payload",
                        JSON.stringify({ kind: "input", type: b.type, label: b.label, value: "" })
                      );
                    }

                    if (b.type.startsWith("op_")) {
                      const symbol = OPERATOR_SYMBOLS[b.type] ?? b.label;
                      e.dataTransfer.setData(
                        "mini/payload",
                        JSON.stringify({
                          kind: "operator",
                          type: b.type,
                          label: b.label,
                          expression: `(undefined ${symbol} undefined)`,
                          expressionNode: {
                            type: b.type,
                            label: b.label,
                            operator: symbol,
                            args: [
                              { mode: "literal", value: "" },
                              { mode: "literal", value: "" }
                            ]
                          }
                        })
                      );
                    }
                  }}
                  style={paletteMiniBlockStyle(meta.accent, groupKey)}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.transform = "translateY(-1px)";
                    event.currentTarget.style.boxShadow = `0 9px 22px ${meta.accent}24`;
                    event.currentTarget.style.background = `${meta.accent}48`;
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.transform = "translateY(0)";
                    event.currentTarget.style.boxShadow = `0 3px 10px ${meta.accent}18`;
                    event.currentTarget.style.background = `${meta.accent}38`;
                  }}
                  title="Drag into a node"
                >
                  <div style={paletteMiniBlockInnerStyle}>
                    <span style={blockAccentDotStyle(meta.accent)} />
                    <PaletteBlockPreview block={b} groupKey={groupKey} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const BENCHMARK_COLORS = ["#f59e0b", "#06b6d4", "#ec4899", "#22c55e", "#f97316"];

function EquityPlaceholderChart({ equity, benchmarks, summaryOnly = false }) {
  const benchmarkNames = useMemo(() => Object.keys(benchmarks?.series ?? {}).filter((name) => benchmarks.series[name]?.length), [benchmarks]);
  const [selectedSeries, setSelectedSeries] = useState(() => ["Strategy", benchmarkNames.includes("SPY") ? "SPY" : benchmarkNames[0]].filter(Boolean));
  const series = useMemo(() => {
    const rawSeries = { Strategy: equity ?? [], ...(benchmarks?.series ?? {}) };
    return Object.entries(rawSeries).reduce((result, [name, values]) => {
      if (!values?.length) return result;
      const base = Number(values[0]?.value);
      if (!Number.isFinite(base) || base === 0) return result;
      const indexed = values.map((point) => ({ ...point, value: (Number(point.value) / base) * 100 })).filter((point) => Number.isFinite(point.value));
      const maxPoints = 140;
      result[name] = indexed.length <= maxPoints
        ? indexed
        : Array.from({ length: maxPoints }, (_, index) => indexed[Math.round(index * (indexed.length - 1) / (maxPoints - 1))]);
      return result;
    }, {});
  }, [equity, benchmarks]);
  const colors = useMemo(() => ({ Strategy: "#4e27d8", ...Object.fromEntries(benchmarkNames.map((name, index) => [name, BENCHMARK_COLORS[index % BENCHMARK_COLORS.length]])) }), [benchmarkNames]);
  const visibleSeries = selectedSeries.filter((name) => series[name]?.length);
  const paths = useMemo(() => {
    const width = 312;
    const height = 126;
    const values = visibleSeries.flatMap((name) => series[name].map((entry) => entry.value));
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 1;
    const range = Math.max(1, max - min);
    return visibleSeries.map((name) => ({
      name,
      points: series[name].map((entry, index) => {
        const x = series[name].length === 1 ? 0 : (index / (series[name].length - 1)) * width;
        const y = height - ((entry.value - min) / range) * height;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(" ")
    }));
  }, [series, visibleSeries]);
  const toggleSeries = (name) => setSelectedSeries((current) => current.includes(name)
    ? current.filter((item) => item !== name)
    : [...current, name]);
  const dateSeries = series[visibleSeries[0]] ?? series.Strategy ?? [];

  return (
    <div style={equityChartBoxStyle}>
      <div style={equityChartHeaderStyle}>
        <span>Portfolio growth</span>
      </div>
      <div style={benchmarkTagsStyle} aria-label="Chart series">
        {["Strategy", ...benchmarkNames].map((name) => {
          const active = selectedSeries.includes(name);
          return <button key={name} type="button" aria-pressed={active} onClick={() => toggleSeries(name)} style={benchmarkTagStyle(active, colors[name])}>
            <span style={benchmarkTagDotStyle(colors[name])} />{name}
          </button>;
        })}
      </div>
      <div style={equityChartInnerStyle}>
        <svg viewBox="0 0 312 126" preserveAspectRatio="none" style={equitySvgStyle}>
          {paths.map((path) => <polyline key={path.name} points={path.points} fill="none" stroke={colors[path.name]} strokeWidth={path.name === "Strategy" ? 3 : 2} vectorEffect="non-scaling-stroke" />)}
        </svg>
        {!paths.length && <div style={emptyChartOverlayStyle}>Select at least one series</div>}
      </div>
      <div style={equityChartFooterStyle}>
        <span>{dateSeries[0]?.label ?? equity?.[0]?.label ?? "Start"}</span>
        <span>{dateSeries[dateSeries.length - 1]?.label ?? equity?.[equity.length - 1]?.label ?? "End"}</span>
      </div>
      {summaryOnly && !equity?.length && <div style={{ marginTop: 8, color: "#64748b", fontSize: 10, lineHeight: 1.4 }}>No strategy equity points were included in this response.</div>}
    </div>
  );
}

const METRIC_HELP = {
  "Total return": "Total percentage gain or loss over the full backtest. Higher is better, but always consider the time period and risk taken.",
  CAGR: "Compound annual growth rate. It converts the full-period return into an annualized rate. Rough guide: 8–12% is historically strong for a diversified long-term strategy; consistency matters more than one high result.",
  Sharpe: "Return earned per unit of volatility. Below 1 is modest, 1–2 is good, and above 2 is very strong. Treat unusually high values cautiously.",
  "Max drawdown": "Largest peak-to-trough portfolio decline. Closer to 0% is better; below −20% can be difficult to tolerate, while −40% or worse signals substantial risk.",
  Volatility: "Annualized variability of returns. Lower is steadier, but the appropriate level depends on the strategy. Broad equity portfolios often sit around 15–25%.",
  "Win rate": "Percentage of completed trades that were profitable. It is not meaningful alone: a low win rate can work when winners are much larger than losers."
};

function MetricCard({ label, value, hint, tone = "neutral", help = METRIC_HELP[label] }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const helpButtonRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);

  useEffect(() => {
    if (!helpOpen || !helpButtonRef.current) {
      setTooltipPosition(null);
      return undefined;
    }

    const updateTooltipPosition = () => {
      const rect = helpButtonRef.current?.getBoundingClientRect();
      if (!rect) return;

      const tooltipWidth = 210;
      const viewportPadding = 8;
      const centeredLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
      setTooltipPosition({
        left: Math.min(
          window.innerWidth - tooltipWidth - viewportPadding,
          Math.max(viewportPadding, centeredLeft)
        ),
        top: rect.top - 7
      });
    };

    updateTooltipPosition();
    window.addEventListener("resize", updateTooltipPosition);
    window.addEventListener("scroll", updateTooltipPosition, true);
    return () => {
      window.removeEventListener("resize", updateTooltipPosition);
      window.removeEventListener("scroll", updateTooltipPosition, true);
    };
  }, [helpOpen]);

  return (
    <div style={metricCardStyle(tone)}>
      <div style={metricLabelRowStyle}>
        <div style={metricLabelStyle} title={label}>{label}</div>
        {help && <span style={metricHelpWrapStyle}>
          <button ref={helpButtonRef} type="button" aria-label={`About ${label}`} aria-expanded={helpOpen} onClick={() => setHelpOpen((open) => !open)} onMouseEnter={() => setHelpOpen(true)} onMouseLeave={() => setHelpOpen(false)} onFocus={() => setHelpOpen(true)} onBlur={() => setHelpOpen(false)} style={metricHelpButtonStyle}>i</button>
          {helpOpen && tooltipPosition && createPortal(
            <span role="tooltip" style={metricHelpTooltipStyle(tooltipPosition)}>{help}</span>,
            document.body
          )}
        </span>}
      </div>
      <div style={metricValueStyle}>{value}</div>
      {hint && <div style={metricHintStyle}>{hint}</div>}
    </div>
  );
}

function SimulationResultsPanel({ result }) {
  if (!result) {
    return (
      <div style={emptyResultsStyle}>
        <div style={emptyResultsIconStyle}>▶</div>
        <div style={emptyResultsTitleStyle}>No simulation yet</div>
        <p style={emptyResultsTextStyle}>
          Press play to generate a local randomized preview from the current blocks.
        </p>
      </div>
    );
  }

  const { metrics, strategySummary } = result;
  const netGain = Number.isFinite(metrics.netProfit) ? metrics.netProfit : Number.isFinite(metrics.finalValue) && Number.isFinite(strategySummary.initialCapital) ? metrics.finalValue - strategySummary.initialCapital : null;

  return (
    <div style={resultsContentStyle}>
      <div style={resultsHeroStyle}>
        <div style={resultsHeroMainStyle}>
          <div style={resultsTitleStyle}>{result.displayName || "Backtest results"}</div>
          <div style={resultsGeneratedStyle}>Updated {result.generatedAt}</div>
        </div>
      </div>

      <div style={resultsSectionHeaderStyle}>
        <span>Performance</span>
      </div>
      <div style={metricGridStyle}>
        <MetricCard label="Final value" value={formatCurrency(metrics.finalValue)} hint={Number.isFinite(netGain) ? `${netGain >= 0 ? "+" : ""}${formatCurrency(netGain)} net` : "Net profit N/A"} tone={Number.isFinite(netGain) && netGain < 0 ? "negative" : "positive"} />
        <MetricCard label="Total return" value={formatPercent(metrics.totalReturn)} hint="Cumulative" tone={Number.isFinite(metrics.totalReturn) && metrics.totalReturn < 0 ? "negative" : "positive"} />
      </div>

      <EquityPlaceholderChart equity={result.equity} benchmarks={result.benchmarks} summaryOnly={result.equityIsSummary} />

      <div style={resultsSectionHeaderStyle}>
        <span>Risk & return</span>
      </div>
      <div style={metricGridStyle}>
        <MetricCard label="CAGR" value={formatPercent(metrics.cagr)} />
        <MetricCard label="Sharpe" value={formatMetricNumber(metrics.sharpe)} />
        <MetricCard label="Max drawdown" value={formatPercent(metrics.maxDrawdown)} tone="negative" />
        <MetricCard label="Volatility" value={formatPercent(metrics.volatility)} />
      </div>

      <div style={resultsSectionHeaderStyle}>
        <span>Activity</span>
      </div>
      <div style={secondaryMetricGridStyle}>
        <MetricCard label="Win rate" value={formatPercent(metrics.winRate, 0)} />
        <MetricCard label="Rebalances" value={metrics.rebalances} />
        <MetricCard label="Trades" value={metrics.trades} />
      </div>

      <div style={strategyNarrativeStyle}>
        <div style={strategySummaryTitleStyle}>How this strategy works</div>
        <p style={strategyNarrativeTextStyle}>
          {result.deterministicSummary?.text || "A strategy summary was not saved with this backtest."}
        </p>
      </div>

      <div style={strategySummaryStyle}>
        <div style={strategySummaryTitleStyle}>Strategy snapshot</div>
        <div style={summaryRowStyle}>
          <span>Initial capital</span>
          <strong style={summaryValueStyle}>{formatCurrency(strategySummary.initialCapital)}</strong>
        </div>
        <div style={summaryRowStyle}>
          <span>Target positions</span>
          <strong style={summaryValueStyle}>{strategySummary.targetPositions ?? "N/A"}</strong>
        </div>
        <div style={summaryRowStyle}>
          <span>Rebalance days</span>
          <strong style={summaryValueStyle}>{strategySummary.rebalanceDays ?? "N/A"}</strong>
        </div>
        <div style={summaryRowStyle}>
          <span>Conditions</span>
          <strong style={summaryValueStyle}>{strategySummary.conditionCount ?? "N/A"}</strong>
        </div>
        <div style={summaryRowStyle}>
          <span>Ranking</span>
          <strong style={summaryValueStyle}>{strategySummary.rankingVariable ?? "N/A"}</strong>
        </div>
        <div style={summaryRowStyle}>
          <span>Direction</span>
          <strong style={summaryValueStyle}>{strategySummary.rankingDirection ?? "N/A"}</strong>
        </div>
      </div>
    </div>
  );
}

function HistoryLoadingSkeleton() {
  return (
    <div style={historySkeletonListStyle} role="status" aria-label="Loading backtests">
      {[0, 1, 2].map((item) => (
        <div key={item} style={historySkeletonCardStyle}>
          <div style={historySkeletonTopStyle}><Skeleton width="35%" height={18} /><Skeleton width="29%" height={15} /></div>
          <Skeleton width="58%" height={24} sx={{ mt: 0.5 }} />
          <Skeleton width="48%" height={16} />
          <div style={historySkeletonMetricsStyle}>{[0, 1, 2, 3].map((metric) => <Skeleton key={metric} variant="rounded" height={34} />)}</div>
        </div>
      ))}
    </div>
  );
}

const formatBacktestStatus = (status) => ({ submitting: "Submitting", queued: "Queued", pending: "Pending", running: "Running", completed: "Completed", failed: "Failed", cancelled: "Cancelled", canceled: "Cancelled" })[status] ?? status ?? "Pending";

// eslint-disable-next-line no-unused-vars -- retained temporarily for compatibility with session-history callers
function BacktestHistoryPanel({ runs, onOpenResult }) {
  if (!runs.length) return (
    <div style={emptyResultsStyle}>
      <div style={emptyHistoryIconStyle}>↻</div>
      <div style={emptyResultsTitleStyle}>No backtests yet</div>
      <p style={emptyResultsTextStyle}>Runs started from this Lab session will appear here, including pending and completed backtests.</p>
    </div>
  );

  return (
    <div style={historyContentStyle}>
      <div style={historyHeaderStyle}>
        <div><div style={resultsEyebrowStyle}>Backtest activity</div><div style={historyTitleStyle}>Run history</div></div>
        <span style={historyCountStyle}>{runs.length}</span>
      </div>
      <div style={historyListStyle}>{runs.map((run) => {
        const completed = run.status === "completed" && run.result;
        const returnValue = run.result?.metrics?.totalReturn;
        return (
          <button key={run.key} type="button" disabled={!completed} onClick={() => completed && onOpenResult(run.result)} style={historyRunStyle(completed)}>
            <div style={historyRunTopStyle}>
              <span style={historyStatusStyle(run.status)}><span style={historyStatusDotStyle(run.status)} />{formatBacktestStatus(run.status)}</span>
              <span style={historyDateStyle}>{run.createdAt}</span>
            </div>
            <div style={historyPeriodStyle}>{run.startDate} <span>→</span> {run.endDate}</div>
            <div style={historyRunBottomStyle}>
              <span>{run.id ? `#${String(run.id).slice(0, 8)}` : "Submitting request"}</span>
              {Number.isFinite(returnValue) && <strong style={{ color: returnValue >= 0 ? "#15803d" : "#b91c1c" }}>{formatPercent(returnValue)}</strong>}
              {completed && <span style={historyOpenStyle}>View results →</span>}
            </div>
            {run.error && <div style={historyErrorStyle}>{run.error}</div>}
          </button>
        );
      })}</div>
    </div>
  );
}

const ACTIVE_BACKTEST_STATES = new Set(["queued", "starting", "running"]);
const HISTORY_STATUS_OPTIONS = ["", "queued", "starting", "running", "completed", "failed", "cancelled"];
const formatHistoryDate = (value, dateOnly = false) => {
  if (!value) return "—";
  if (dateOnly && typeof value === "string") return value.slice(0, 10);
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? String(value) : date.toLocaleString();
};
const historyMetric = (metrics, keys) => {
  const key = keys.find((candidate) => Number.isFinite(Number(metrics?.[candidate])));
  return key ? Number(metrics[key]) : null;
};

function PastBacktestsPanel({ refreshVersion, onOpenResult, onRestoreStrategy }) {
  const [runs, setRuns] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openingId, setOpeningId] = useState("");
  const [restoringId, setRestoringId] = useState("");
  const [openError, setOpenError] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editingName, setEditingName] = useState("");
  const [savingId, setSavingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const hasActiveRuns = runs.some((run) => ACTIVE_BACKTEST_STATES.has(run.status));

  const loadHistory = useCallback(async (signal, quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const payload = await getBacktests({ page, limit: 20, status: statusFilter, signal });
      setRuns(Array.isArray(payload.backtests) ? payload.backtests : []);
      setPagination(payload.pagination ?? { page, total: 0, totalPages: 1 });
      setError("");
    } catch (requestError) {
      if (requestError.code !== "ERR_CANCELED") setError(getBacktestErrorMessage(requestError));
    } finally {
      if (!signal?.aborted && !quiet) setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    const controller = new AbortController();
    loadHistory(controller.signal);
    return () => controller.abort();
  }, [loadHistory, refreshVersion]);

  useEffect(() => {
    if (!hasActiveRuns) return undefined;
    const timer = setInterval(() => loadHistory(undefined, true), BACKTEST_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasActiveRuns, loadHistory]);

  const handleOpen = async (run) => {
    setOpeningId(run.id); setOpenError("");
    try {
      const payload = await getBacktest(run.id, undefined, { holdingsDates: 100 });
      onOpenResult(normalizeBacktestResponse(payload));
    } catch (requestError) {
      setOpenError(getBacktestErrorMessage(requestError));
    } finally { setOpeningId(""); }
  };
  const handleRestore = async (run) => {
    setRestoringId(run.id); setOpenError("");
    try {
      const payload = await getBacktest(run.id);
      const normalized = normalizeBacktestResponse(payload);
      if (!normalized.editorState?.nodes || !normalized.editorState?.edges) {
        throw new Error("This backtest does not contain restorable blocks. Only newer backtests saved with editor state can be restored.");
      }
      onRestoreStrategy(normalized.editorState);
    } catch (requestError) {
      setOpenError(requestError.response ? getBacktestErrorMessage(requestError) : requestError.message);
    } finally { setRestoringId(""); }
  };
  const handleRename = async (run) => {
    const name = editingName.trim();
    if (!name) return;
    setSavingId(run.id); setOpenError("");
    try {
      const payload = await renameBacktest(run.id, name);
      const displayName = payload?.backtest?.display_name ?? payload?.display_name ?? name;
      setRuns((current) => current.map((item) => item.id === run.id ? { ...item, display_name: displayName } : item));
      setEditingId("");
    } catch (requestError) { setOpenError(getBacktestErrorMessage(requestError)); }
    finally { setSavingId(""); }
  };
  const handleDelete = async (run) => {
    if (!window.confirm(`Delete “${run.display_name || "Untitled backtest"}”? This cannot be undone.`)) return;
    setDeletingId(run.id); setOpenError("");
    try {
      await deleteBacktest(run.id);
      setRuns((current) => current.filter((item) => item.id !== run.id));
      setPagination((current) => ({ ...current, total: Math.max(0, current.total - 1) }));
    } catch (requestError) { setOpenError(getBacktestErrorMessage(requestError)); }
    finally { setDeletingId(""); }
  };

  return (
    <section style={historyContentStyle} aria-labelledby="past-backtests-title">
      <div style={historyHeaderStyle}>
        <div><div id="past-backtests-title" style={historyTitleStyle}>Past Backtests</div></div>
        <span style={historyCountStyle} aria-label={`${pagination.total} backtests`}>{pagination.total}</span>
      </div>
      <div style={historyFilterStyle}>
        <LabSelect
          value={statusFilter}
          onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}
          options={HISTORY_STATUS_OPTIONS.map((status) => ({ value: status, label: status ? formatBacktestStatus(status) : "All statuses" }))}
          displayEmpty
          renderValue={(selected) => selected ? formatBacktestStatus(selected) : "All statuses"}
          compact
          accent="#4e27d8"
          inputProps={{ "aria-label": "Filter backtests by status" }}
          sx={historyStatusSelectSx}
        />
      </div>
      {error && <div role="alert" style={historyErrorStateStyle}><span>{error}</span><button type="button" onClick={() => loadHistory()} style={historyRetryStyle}>Retry</button></div>}
      {openError && <div role="alert" style={historyErrorStateStyle}>{openError}</div>}
      {loading ? <HistoryLoadingSkeleton /> : !error && !runs.length ? (
        <div style={emptyResultsStyle}><div style={emptyHistoryIconStyle}>↻</div><div style={emptyResultsTitleStyle}>No backtests found</div><p style={emptyResultsTextStyle}>{statusFilter ? "No runs match this status filter." : "Your submitted backtests will appear here."}</p></div>
      ) : <div style={historyListStyle}>{runs.map((run) => {
        const metrics = run.metrics_json ?? {};
        const totalReturn = historyMetric(metrics, ["total_return_percent", "totalReturnPercent"]);
        const cagr = historyMetric(metrics, ["cagr_percent", "cagrPercent"]);
        const sharpe = historyMetric(metrics, ["sharpe_ratio", "sharpe_rough", "sharpe"]);
        const canOpen = run.status === "completed";
        const isActive = ACTIVE_BACKTEST_STATES.has(run.status);
        return (
          <article key={run.id} style={historyRunStyle(canOpen)}>
            <div style={historyRunTopStyle}><span style={historyStatusStyle(run.status)}><span style={historyStatusDotStyle(run.status)} />{formatBacktestStatus(run.status)}</span><span style={historyDateStyle}>{formatHistoryDate(run.created_at)}</span></div>
            {editingId === run.id ? <form style={historyRenameStyle} onSubmit={(event) => { event.preventDefault(); handleRename(run); }}>
              <label style={historySrOnlyStyle} htmlFor={`backtest-name-${run.id}`}>Backtest name</label>
              <input id={`backtest-name-${run.id}`} autoFocus value={editingName} onChange={(event) => setEditingName(event.target.value)} maxLength={191} style={historyRenameInputStyle} />
              <button type="submit" disabled={!editingName.trim() || savingId === run.id} style={historyActionButtonStyle}>Save</button>
              <button type="button" onClick={() => setEditingId("")} style={historyActionButtonStyle}>Cancel</button>
            </form> : <div style={historyNameRowStyle}><strong style={historyNameStyle}>{run.display_name || "Untitled backtest"}</strong></div>}
            <div style={historyPeriodStyle}>{formatHistoryDate(run.start_date, true)} <span>→</span> {formatHistoryDate(run.end_date, true)}</div>
            {isActive && <div style={historyRunningStyle}><CircularProgress size={14} thickness={5} sx={{ color: "#6d4aff" }} /><div><strong style={historyRunningTitleStyle}>Backtest in progress</strong><span style={historyRunningTextStyle}>Results will appear automatically.</span></div></div>}
            <div style={{ ...historyMetricsStyle, opacity: isActive ? 0.6 : 1 }}>
              <span style={historyMetricItemStyle}>Return<strong>{totalReturn === null ? "N/A" : `${totalReturn.toFixed(1)}%`}</strong></span>
              <span style={historyMetricItemStyle}>CAGR<strong>{cagr === null ? "N/A" : `${cagr.toFixed(1)}%`}</strong></span>
              <span style={historyMetricItemStyle}>Sharpe<strong>{sharpe === null ? "N/A" : sharpe.toFixed(2)}</strong></span>
              <span style={historyMetricItemStyle}>Trades<strong>{run.trade_count ?? metrics.trade_count ?? "N/A"}</strong></span>
            </div>
            <div style={historyRunBottomStyle}>
              {canOpen && <button type="button" onClick={() => handleOpen(run)} disabled={openingId === run.id} style={historyOpenButtonStyle}>{openingId === run.id ? "Loading…" : "View results →"}</button>}
              <button type="button" onClick={() => { setEditingId(run.id); setEditingName(run.display_name || ""); }} style={historyTextButtonStyle}>Rename</button>
              <button type="button" onClick={() => handleRestore(run)} disabled={restoringId === run.id} style={historyTextButtonStyle}>{restoringId === run.id ? "Restoring…" : "Restore blocks"}</button>
              <button type="button" onClick={() => handleDelete(run)} disabled={deletingId === run.id} style={historyDeleteButtonStyle}>{deletingId === run.id ? "Deleting…" : "Delete"}</button>
            </div>
            {run.error_message && <div style={historyErrorStyle}>{run.error_message}</div>}
          </article>
        );
      })}</div>}
      {!loading && !error && pagination.totalPages > 1 && <nav style={historyPaginationStyle} aria-label="Backtest history pages">
        <button type="button" style={historyPageButtonStyle} disabled={!pagination.hasPreviousPage} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button>
        <span>Page {pagination.page} of {pagination.totalPages}</span>
        <button type="button" style={historyPageButtonStyle} disabled={!pagination.hasNextPage} onClick={() => setPage((value) => value + 1)}>Next</button>
      </nav>}
    </section>
  );
}

function LabSidebar({ activePanel, setActivePanel, simulationResult, selectedSection, historyRefreshVersion, onOpenHistoryResult, onRestoreStrategy }) {
  return (
    <div style={paletteStyle}>
      <div style={sidebarTopStyle}>
        <button
          type="button"
          style={sidebarTabStyle(activePanel === "blocks")}
          onClick={() => setActivePanel("blocks")}
        >
          Blocks
        </button>
        <button
          type="button"
          style={sidebarTabStyle(activePanel === "results")}
          onClick={() => setActivePanel("results")}
        >
          Results
        </button>
        <button type="button" style={sidebarTabStyle(activePanel === "history")} onClick={() => setActivePanel("history")}>
          History
        </button>
      </div>

      {activePanel === "history" ? (
        <PastBacktestsPanel refreshVersion={historyRefreshVersion} onOpenResult={onOpenHistoryResult} onRestoreStrategy={onRestoreStrategy} />
      ) : activePanel === "results" ? (
        <SimulationResultsPanel result={simulationResult} />
      ) : (
        <>
          <div style={paletteContextStyle}>
            {selectedSection === "execution"
              ? "Preferences are configured directly in the node."
              : selectedSection
              ? `Showing blocks for ${SECTION_TITLES[selectedSection]}`
              : "Select a pipeline stage to focus its blocks."}
          </div>
          <BlockPaletteContent selectedSection={selectedSection} />
        </>
      )}
    </div>
  );
}

function BlockCardHeader({ block, accent, showGrip = true }) {
  return (
    <div style={blockHeaderRowStyle}>
      <div style={blockTitleWrapStyle}>
        <span style={blockFieldTitleStyle}>{block.label}</span>
      </div>
      {showGrip && <span style={dragGripStyle}>...</span>}
    </div>
  );
}

function LabSelect({ value, onChange, options, compact = false, accent = "#7c3aed", sx, ...props }) {
  return (
    <Select
      {...props}
      value={value}
      onChange={onChange}
      size="small"
      variant="outlined"
      MenuProps={{
        PaperProps: {
          sx: {
            mt: 0.75,
            p: 0.75,
            borderRadius: "14px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 18px 45px rgba(15,23,42,0.16)",
            "& .MuiMenuItem-root": {
              minHeight: 34,
              px: 1.25,
              borderRadius: "9px",
              fontFamily: "Poppins, system-ui, sans-serif",
              fontSize: 11,
              fontWeight: 600,
              color: "#334155",
              "&.Mui-selected": {
                backgroundColor: `${accent}1f`,
                color: accent
              },
              "&.Mui-selected:hover": { backgroundColor: `${accent}2b` }
            }
          }
        }
      }}
      sx={{
        width: "100%",
        height: compact ? 30 : 34,
        borderRadius: "999px",
        backgroundColor: "rgba(255,255,255,0.88)",
        color: "#172033",
        fontFamily: "Poppins, system-ui, sans-serif",
        fontSize: 11,
        fontWeight: 650,
        boxShadow: "0 2px 7px rgba(15,23,42,0.07)",
        "& .MuiSelect-select": {
          py: 0.65,
          pl: 1.35,
          pr: "30px !important"
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(100,116,139,0.28)"
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: accent
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: accent,
          borderWidth: 2
        },
        "&.Mui-focused": {
          boxShadow: `0 0 0 3px ${accent}20, 0 3px 9px ${accent}20`
        },
        "& .MuiSvgIcon-root": {
          right: 8,
          color: accent,
          fontSize: 19
        },
        ...sx
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}

function formatCompactInputValue(value) {
  if (value === "" || value == null) return "";
  const numericValue = Number(String(value).replaceAll(",", ""));
  if (!Number.isFinite(numericValue) || Math.abs(numericValue) < 1000) return String(value);
  return new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1
  }).format(numericValue);
}

function CompactNumberInput({ value, onValueChange, title, placeholder }) {
  const [focused, setFocused] = useState(false);

  return (
    <input
      className="nodrag"
      type="text"
      inputMode="decimal"
      value={focused ? value : formatCompactInputValue(value)}
      title={title}
      placeholder={placeholder}
      onMouseDown={keepInputFocus}
      onPointerDown={keepInputFocus}
      onKeyDown={stopNodeDeletionKey}
      onFocus={(event) => {
        setFocused(true);
        requestAnimationFrame(() => event.target.select());
      }}
      onBlur={() => setFocused(false)}
      onChange={(event) => onValueChange(event.target.value.replaceAll(",", ""))}
      style={{ ...settingInlineInputStyle, textAlign: "right", paddingRight: 10 }}
    />
  );
}

/* =====================================================
   OPERATOR UI: 2 args (literal OR dropped variable)
===================================================== */

function OperatorBlockUI({ block, accent, onUpdateBlock }) {
  const setArg = (idx, nextArg) => {
    onUpdateBlock(block.id, {
      ...block,
      args: (block.args ?? []).map((a, i) => (i === idx ? nextArg : a))
    });
  };

  return (
    <div style={operatorRowStyle}>
      <ArgSlot
        arg={block.args?.[0]}
        accent={accent}
        onChange={(next) => setArg(0, next)}
        placeholder=""
        suffix={block.args?.[1]?.mode === "var" && PERCENT_VARIABLE_TYPES.has(block.args[1].varType) ? "%" : ""}
      />
      <div style={operatorSymbolStyle}>{block.label}</div>
      <ArgSlot
        arg={block.args?.[1]}
        accent={accent}
        onChange={(next) => setArg(1, next)}
        placeholder=""
        suffix={block.args?.[0]?.mode === "var" && PERCENT_VARIABLE_TYPES.has(block.args[0].varType) ? "%" : ""}
      />
    </div>
  );
}

function BlockFieldsUI({ block, accent, onUpdateBlock }) {
  const fieldDefs = BLOCK_FIELDS[block.type] ?? [];

  if (!fieldDefs.length) {
    return (
      <div style={blockFieldStackStyle}>
        <BlockCardHeader block={block} accent={accent} />
      </div>
    );
  }

  const setField = (key, nextValue) => {
    onUpdateBlock(block.id, {
      ...block,
      fields: {
        ...(block.fields ?? {}),
        [key]: nextValue
      }
    });
  };

  const renderFieldControl = (field, compact = false) => {
    const value = block.fields?.[field.key]?.value ?? "";

    if (field.inputType === "bool") {
      const checked = value === "true";

      return (
        <LabSelect
          className="nodrag"
          value={checked ? "true" : "false"}
          onMouseDown={keepInputFocus}
          onPointerDown={keepInputFocus}
          onKeyDown={stopNodeDeletionKey}
          onChange={(e) => setField(field.key, { mode: "literal", value: e.target.value })}
          compact={compact}
          accent={accent}
          options={[{ value: "false", label: "No" }, { value: "true", label: "Yes" }]}
        />
      );
    }

    if (field.inputType === "select") {
      return (
        <LabSelect
          className="nodrag"
          value={value}
          onMouseDown={keepInputFocus}
          onPointerDown={keepInputFocus}
          onKeyDown={stopNodeDeletionKey}
          onChange={(e) => setField(field.key, { mode: "literal", value: e.target.value })}
          compact={compact}
          accent={accent}
          options={field.options ?? []}
        />
      );
    }

    if (compact) {
      if (
        COMPACT_UNIVERSE_NUMBER_TYPES.has(block.type) &&
        (field.inputType === "int" || field.inputType === "number")
      ) {
        return (
          <CompactNumberInput
            value={value}
            title={value ? String(value) : field.label}
            placeholder={field.label}
            onValueChange={(nextValue) => setField(field.key, { mode: "literal", value: nextValue })}
          />
        );
      }

      return (
        <input
          className="nodrag"
          type={field.inputType === "int" || field.inputType === "number" ? "number" : "text"}
          inputMode={field.inputType === "int" || field.inputType === "number" ? "decimal" : undefined}
          value={value}
          title={value ? String(value) : field.label}
          onMouseDown={keepInputFocus}
          onPointerDown={keepInputFocus}
          onKeyDown={stopNodeDeletionKey}
          onChange={(e) => setField(field.key, { mode: "literal", value: e.target.value })}
          placeholder={field.label}
          style={settingInlineInputStyle}
        />
      );
    }

    return (
      <ArgSlot
        arg={block.fields?.[field.key]}
        accent={accent}
        onChange={(next) => setField(field.key, next)}
        placeholder={field.label}
        suffix={field.key === "threshold" && PERCENT_THRESHOLD_BLOCK_TYPES.has(block.type) ? "%" : ""}
      />
    );
  };

  if (block.fixed && fieldDefs.length === 1) {
    return (
      <div style={settingInlineRowStyle}>
        <div style={settingInlineTitleStyle}>
          <span style={settingInlineLabelStyle}>{block.label}</span>
        </div>
        <div
          style={{
            ...settingInlineControlStyle,
            ...(fieldDefs[0].inputType === "select" ? { flexBasis: 148, minWidth: 148 } : {})
          }}
        >
          {renderFieldControl(fieldDefs[0], true)}
        </div>
      </div>
    );
  }

  if (fieldDefs.length === 1) {
    const scratchLabels = SCRATCH_BLOCK_LABELS[block.type];

    if (scratchLabels) {
      const isInlineUniverseFilter = !scratchLabels[1];
      const isTopFilter = block.type === "top_mcap" || block.type === "top_volume";
      return (
        <div
          style={
            isInlineUniverseFilter
              ? universeFilterRowStyle
              : isTopFilter
                ? topFilterRowStyle
                : scratchBlockRowStyle
          }
        >
          <span style={scratchBlockTextStyle}>{scratchLabels[0]}</span>
          <div
            style={
              isInlineUniverseFilter
                ? universeFilterInputWrapStyle
                : isTopFilter
                  ? topFilterInputWrapStyle
                  : scratchBlockInputWrapStyle
            }
          >
            {renderFieldControl(fieldDefs[0], true)}
          </div>
          {scratchLabels[1] && <span style={scratchBlockTextStyle}>{scratchLabels[1]}</span>}
        </div>
      );
    }

    return (
      <div style={compactBlockRowStyle}>
        <div style={compactBlockTitleStyle}>
          <span style={blockFieldTitleStyle}>{block.label}</span>
        </div>
        <div style={compactBlockControlStyle}>{renderFieldControl(fieldDefs[0])}</div>
      </div>
    );
  }

  return (
    <div style={blockFieldStackStyle}>
      <BlockCardHeader block={block} accent={accent} />
      {fieldDefs.map((field) => (
        <div key={field.key} style={blockFieldRowStyle}>
          <span style={blockFieldLabelStyle}>{field.label}</span>
          {renderFieldControl(field)}
        </div>
      ))}
    </div>
  );
}

function InputBlockUI({ block, onUpdateBlock }) {
  const value = block.fields?.value?.value ?? "";
  const accent = PALETTE_GROUPS.inputs?.accent ?? "#64748B";

  return (
    <div style={inputBlockStyle}>
      <BlockCardHeader block={block} accent={accent} showGrip={false} />
      <input
        className="nodrag"
        value={value}
        onMouseDown={keepInputFocus}
        onPointerDown={keepInputFocus}
        onKeyDown={stopNodeDeletionKey}
        onChange={(e) =>
          onUpdateBlock(block.id, {
            ...block,
            fields: { value: { mode: "literal", value: e.target.value } }
          })
        }
        placeholder={block.type === "input_number" ? "0" : "text"}
        style={inputBlockInputStyle}
      />
    </div>
  );
}

function ArgSlot({ arg, accent, onChange, placeholder = "value", suffix = "" }) {
  const fullLabel = arg?.mode === "expr" ? arg.label ?? arg.value : arg?.label;
  const graphLabel = arg?.mode === "var"
    ? GRAPH_VARIABLE_LABELS[arg.varType] ?? fullLabel
    : fullLabel;

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // preferred: mini/payload (variable)
    const payload = e.dataTransfer.getData("mini/payload");
    if (payload) {
      try {
        const parsed = JSON.parse(payload);
        if (parsed?.kind === "variable") {
          onChange({ mode: "var", varType: parsed.type, label: parsed.label });
          return;
        }
        if (parsed?.kind === "input") {
          onChange({ mode: "literal", value: parsed.value ?? "" });
          return;
        }
        // Operators cannot be nested inside another operator's argument slot.
        if (parsed?.kind === "operator") return;
      } catch {}
    }

    // fallback: block/type (variable from palette)
    const type = e.dataTransfer.getData("block/type");
    if (type && type.startsWith("var_")) {
      const def = BLOCKS.find((b) => b.type === type);
      onChange({ mode: "var", varType: type, label: def?.label ?? type });
    }

    if (type && type.startsWith("input_")) {
      onChange({ mode: "literal", value: "" });
    }

    // Ignore operator palette drops; slots accept only variables and literals.
    if (type && type.startsWith("op_")) return;
  };

  const clearVar = (e) => {
    e.stopPropagation();
    onChange({ mode: "literal", value: "" });
  };

  return (
    <div
      className="nodrag"
      data-arg-slot="true"
      style={argSlotStyle(accent)}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={onDrop}
      title="Drop a variable or input here, or type a value. Operators cannot be nested."
    >
      {arg?.mode === "var" || arg?.mode === "expr" ? (
        <div
          draggable
          style={argPillStyle(accent)}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onDragStart={(event) => {
            event.stopPropagation();
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData(
              "mini/payload",
              JSON.stringify(
                arg.mode === "var"
                  ? { kind: "variable", type: arg.varType, label: fullLabel }
                  : {
                      kind: "operator",
                      label: fullLabel,
                      expression: arg.value,
                      expressionNode: arg.expression ?? null
                    }
              )
            );
          }}
          onDragEnd={(event) => {
            event.stopPropagation();
            onChange({ mode: "literal", value: "" });
          }}
          title={`${fullLabel}. Drag to move or remove.`}
        >
          <span
            style={argPillLabelStyle}
            title={fullLabel}
          >
            {graphLabel}
          </span>
          <button className="nodrag" onClick={clearVar} style={argClearStyle} title="Clear">
            ×
          </button>
        </div>
      ) : (
        <div style={argInputWrapStyle}>
          <input
            className="nodrag"
            type={suffix ? "number" : "text"}
            inputMode={suffix ? "decimal" : undefined}
            value={arg?.value ?? ""}
            onMouseDown={keepInputFocus}
            onPointerDown={keepInputFocus}
            onKeyDown={stopNodeDeletionKey}
            onChange={(e) => onChange({ mode: "literal", value: e.target.value })}
            placeholder={placeholder}
            style={{ ...argInputStyle, paddingRight: suffix ? 16 : 0 }}
          />
          {suffix && <span aria-hidden="true" style={argInputSuffixStyle}>{suffix}</span>}
        </div>
      )}
    </div>
  );
}

/* =====================================================
   SORTABLE MINI BLOCK (with trash icon)
===================================================== */

function SortableMiniBlock({ block, accent, onRemove, onUpdateBlock }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: block.id, disabled: block.fixed });

  const dragPayload = useMemo(() => {
    if (block.kind === "variable") {
      return JSON.stringify({ kind: "variable", type: block.type, label: block.label });
    }
    if (block.kind === "input") {
      return JSON.stringify({
        kind: "input",
        type: block.type,
        label: block.label,
        value: block.fields?.value?.value ?? ""
      });
    }
    if (block.kind === "operator") {
      return JSON.stringify({
        kind: "operator",
        type: block.type,
        label: block.label,
        expression: formatOperatorExpression(block),
        expressionNode: {
          type: block.type,
          label: block.label,
          operator: OPERATOR_SYMBOLS[block.type] ?? block.label,
          args: block.args ?? []
        }
      });
    }
    return null;
  }, [block]);

  return (
    <div
      ref={setNodeRef}
      {...(block.fixed ? {} : attributes)}
      {...(block.fixed ? {} : listeners)}
      className="nodrag"
      draggable={!block.fixed && !!dragPayload}
      onDragStart={(e) => {
        if (!dragPayload) return;
        e.dataTransfer.setData("mini/payload", dragPayload);
      }}
      style={{
        ...(block.fixed
          ? fixedSettingBlockStyle(accent)
          : block.kind === "operator"
            ? operatorMiniBlockStyle(accent)
            : miniBlockStyle(accent)),
        transform: CSS.Transform.toString(transform),
        transition
      }}
      title={block.fixed ? "Fixed setting" : "Drag to reorder"}
    >
      <span style={blockAccentDotStyle(accent)} />
      <div style={blockContentStyle}>
      {block.kind === "operator" ? (
        <OperatorBlockUI block={block} accent={accent} onUpdateBlock={onUpdateBlock} />
      ) : block.kind === "input" ? (
        <InputBlockUI block={block} onUpdateBlock={onUpdateBlock} />
      ) : (
        <BlockFieldsUI block={block} accent={accent} onUpdateBlock={onUpdateBlock} />
      )}
      </div>

      {!block.fixed && (
        <button
          onClick={(e) => {
          e.stopPropagation();
          onRemove(block.id);
        }}
          title="Remove"
          style={trashButtonStyle}
          className="nodrag"
        >
          <span style={removeButtonTextStyle}>×</span>
        </button>
      )}
    </div>
  );
}

/* =====================================================
   CONTAINER NODE
===================================================== */

function ContainerNode({ id, data, title, subtitle, nodeType, accent, accepts, setNodes }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const blocks = useMemo(() => data?.blocks ?? [], [data?.blocks]);
  const blockIds = useMemo(() => blocks.map((b) => b.id), [blocks]);

  const updateBlock = useCallback(
    (blockId, nextBlock) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  blocks: (n.data?.blocks ?? []).map((b) => (b.id === blockId ? nextBlock : b))
                }
              }
            : n
        )
      );
    },
    [id, setNodes]
  );

  const onDropHtmlBlock = (e) => {
    e.preventDefault();
    if (e.target?.closest?.("[data-arg-slot='true']")) return;

    const type = e.dataTransfer.getData("block/type");
    if (!accepts.includes(type)) return;
    if (
      nodeType === "universe" &&
      (type === "top_mcap" || type === "top_volume") &&
      blocks.some((block) => block.type === "top_mcap" || block.type === "top_volume")
    ) return;

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                blocks: [...(n.data?.blocks ?? []), makeBlock(type)]
              }
            }
          : n
      )
    );
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: { ...n.data, blocks: arrayMove(n.data?.blocks ?? [], oldIndex, newIndex) }
            }
          : n
      )
    );
  };

  const removeBlock = (blockId) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                blocks: (n.data?.blocks ?? []).filter((b) => b.id !== blockId)
              }
            }
          : n
      )
    );
  };

  return (
    <div style={{ position: "relative", paddingBottom: 56 }}>
      <div
        style={nodeStyle(accent, data?.selected)}
        onClick={() => data?.onSelect?.(nodeType)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropHtmlBlock}
      >
        <div style={nodeHeaderStyle(accent)}>
          <span>{title}</span>
          <small style={nodeSubtitleStyle}>{subtitle}</small>
        </div>
        {(nodeType === "signals" || nodeType === "exit") && (
          <label style={matchModeStyle} className="nodrag">
            <span>Rule matching</span>
            <LabSelect
              value={data?.matchMode ?? (nodeType === "signals" ? "all" : "any")}
              onChange={(event) => data?.onMatchModeChange?.(id, event.target.value)}
              compact
              accent={accent}
              sx={{ width: 126, flexShrink: 0 }}
              options={[
                { value: "all", label: "All rules" },
                { value: "any", label: "Any rule" }
              ]}
            />
          </label>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={blockIds} strategy={verticalListSortingStrategy}>
            <div style={blocksContainerStyle} className="nodrag">
              {blocks.map((b) => (
                <SortableMiniBlock
                  key={b.id}
                  block={b}
                  accent={accent}
                  onRemove={removeBlock}
                  onUpdateBlock={updateBlock}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {data?.hasTarget && (
          <Handle type="target" position={Position.Left} style={handleStyle(accent)} />
        )}
        {data?.hasSource && (
          <Handle type="source" position={Position.Right} style={handleStyle(accent)} />
        )}
      </div>

    </div>
  );
}

function UniverseNode(props) {
  return (
    <ContainerNode
      {...props}
      title="Universe"
      subtitle="Where should we look?"
      nodeType="universe"
      accent={NODE_ACCENTS.universe}
      accepts={ACCEPTS.universe}
      setNodes={props.data?.setNodes}
      showPlus={false}
    />
  );
}

function SignalsNode(props) {
  return (
    <ContainerNode
      {...props}
      title="Buy when"
      subtitle="What must be true?"
      nodeType="signals"
      accent={NODE_ACCENTS.signals}
      accepts={ACCEPTS.signals}
      setNodes={props.data?.setNodes}
    />
  );
}

function ExecutionNode(props) {
  return (
    <ContainerNode
      {...props}
      title="Prefer"
      subtitle="Which companies first?"
      nodeType="execution"
      accent={NODE_ACCENTS.execution}
      accepts={ACCEPTS.execution}
      setNodes={props.data?.setNodes}
      showPlus={false}
    />
  );
}

function ExitNode(props) {
  return (
    <ContainerNode
      {...props}
      title="Sell when"
      subtitle="When should we let go?"
      nodeType="exit"
      accent={NODE_ACCENTS.exit}
      accepts={ACCEPTS.exit}
      setNodes={props.data?.setNodes}
    />
  );
}

const NODE_TYPES = {
  universe: UniverseNode,
  signals: SignalsNode,
  execution: ExecutionNode,
  exit: ExitNode
};

/* =====================================================
   FLOW (needs provider)
===================================================== */

const SECTION_TITLES = { universe: "Universe", signals: "Buy when", execution: "Prefer", exit: "Sell when" };
const getSettingValue = (blocks, type, fallback = "") =>
  blocks.find((block) => block.type === type)?.fields?.value?.value ?? fallback;
const updateSettingValue = (setter, type, value) => setter((blocks) => {
  const next = blocks.map((block) => block.type === type
    ? { ...block, fields: { ...block.fields, value: { mode: "literal", value } } }
    : block);
  return next;
});

function SettingsDrawer({ open, onClose, blocks, setBlocks }) {
  const fields = [
    ["setting_initial_capital", "Initial capital"], ["setting_target_positions", "Target positions"],
    ["setting_max_positions", "Maximum positions"], ["setting_min_cash_percent", "Minimum cash percentage"],
    ["setting_rebalance_days", "Rebalance frequency (days)"], ["setting_transaction_fee", "Transaction fee"],
    ["setting_allocation", "Allocation"], ["setting_min_holding_days", "Minimum holding days"],
    ["setting_max_position_size_percent", "Maximum position size"]
  ];
  if (!open) return null;
  return createPortal(
    <div style={drawerOverlayStyle} onMouseDown={onClose}>
      <aside style={drawerStyle} onMouseDown={(event) => event.stopPropagation()}>
        <div style={drawerHeaderStyle}>
          <div><strong style={drawerTitleStyle}>Strategy settings</strong><div style={drawerSubtitleStyle}>Portfolio and backtest preferences</div></div>
          <button type="button" onClick={onClose} style={drawerCloseStyle} aria-label="Close settings">×</button>
        </div>
        <div style={drawerBodyStyle}>
          <div style={drawerFieldsStyle}>
            {fields.map(([type, label], index) => {
              const definition = BLOCK_FIELDS[type]?.[0];
              const value = getSettingValue(blocks, type);
              return (
                <label key={type} style={drawerFieldStyle}>
                  <span>{label}{index >= 8 && <small style={advancedBadgeStyle}>Advanced</small>}</span>
                  {definition?.inputType === "select"
                    ? <LabSelect value={value} onChange={(event) => updateSettingValue(setBlocks, type, event.target.value)} options={definition.options} />
                    : <input type="number" value={value} onChange={(event) => updateSettingValue(setBlocks, type, event.target.value)} style={drawerInputStyle} />}
                </label>
              );
            })}
          </div>
        </div>
      </aside>
    </div>,
    document.body
  );
}

const LAB_TOUR_STEPS = [
  { title: "Build the pipeline", text: "Choose a stage, then drag variables and operators into its cards. The flow reads from Universe through Buy, Prefer, and Sell." },
  { title: "Set realistic assumptions", text: "Choose dates and portfolio settings before running. Percentage fields use familiar values: enter 5 for 5%." },
  { title: "Run and inspect", text: "Run the strategy, follow its status in History, then inspect performance, risk, trades, and holdings in Results." },
  { title: "Save and experiment", text: "Save drafts without running them. Use Ctrl+Z and Ctrl+Y to undo or redo edits while you test ideas." }
];

function LabFirstVisitTour() {
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(() => localStorage.getItem("stocksleuth:lab-tour-seen") !== "1");
  if (!open) return null;
  const finish = () => { localStorage.setItem("stocksleuth:lab-tour-seen", "1"); setOpen(false); };
  const item = LAB_TOUR_STEPS[step];
  return createPortal(
    <div style={tourOverlayStyle} role="dialog" aria-modal="true" aria-labelledby="lab-tour-title">
      <style>{`@keyframes labTourIn{from{opacity:0;transform:translateY(12px) scale(.98)}to{opacity:1;transform:none}}`}</style>
      <div style={tourCardStyle}>
        <div style={tourProgressStyle}>{LAB_TOUR_STEPS.map((_, index) => <span key={index} style={tourProgressDotStyle(index <= step)} />)}</div>
        <div style={tourIllustrationStyle}>{step + 1}</div>
        <div style={resultsEyebrowStyle}>Quick tour · {step + 1} of {LAB_TOUR_STEPS.length}</div>
        <h2 id="lab-tour-title" style={tourTitleStyle}>{item.title}</h2>
        <p style={tourTextStyle}>{item.text}</p>
        <div style={tourActionsStyle}>
          <button type="button" onClick={finish} style={tourSkipStyle}>Skip tour</button>
          {step > 0 && <button type="button" onClick={() => setStep((value) => value - 1)} style={tourSecondaryStyle}>Back</button>}
          <button type="button" onClick={() => step === LAB_TOUR_STEPS.length - 1 ? finish() : setStep((value) => value + 1)} style={tourPrimaryStyle}>{step === LAB_TOUR_STEPS.length - 1 ? "Start building" : "Next"}</button>
        </div>
      </div>
    </div>, document.body
  );
}

function FlowInner({ onSimulationResult, onActivePanelChange, onSelectedSectionChange, onBacktestUpdate, onCreditsChange, credits, restoreSnapshot }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultStart = useMemo(() => { const date = new Date(); date.setUTCFullYear(date.getUTCFullYear() - 3); return date.toISOString().slice(0, 10); }, []);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [runState, setRunState] = useState({ submitting: false, error: "", status: "" });
  const [saveState, setSaveState] = useState("");
  const requestRef = useRef(null);
  const pollTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(pollTimerRef.current), []);
  const [settingBlocks, setSettingBlocks] = useState(() => makeDefaultSettingBlocks());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState("universe");
  const [nodes, setNodes, onNodesChange] = useNodesState([
    { id: "u", type: "universe", position: { x: 30, y: 150 }, data: { blocks: [], hasSource: true } },
    { id: "s1", type: "signals", position: { x: 350, y: 150 }, data: { blocks: [], matchMode: "all", hasTarget: true, hasSource: true } },
    { id: "r1", type: "execution", position: { x: 670, y: 150 }, data: { blocks: makeDefaultRankingBlocks(), hasTarget: true, hasSource: true } },
    { id: "x1", type: "exit", position: { x: 990, y: 150 }, data: { blocks: [], matchMode: "any", hasTarget: true } }
  ]);

  const [edges, setEdges, onEdgesChange] = useEdgesState([
    { id: "u-s1", source: "u", target: "s1" },
    { id: "s1-r1", source: "s1", target: "r1" },
    { id: "r1-x1", source: "r1", target: "x1" }
  ]);

  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const historyRef = useRef({ past: [], present: null, future: [], applying: false, timer: null });
  const makeSnapshot = useCallback(() => ({ nodes, edges, settingBlocks }), [nodes, edges, settingBlocks]);
  const snapshotKey = useCallback((snapshot) => JSON.stringify(snapshot), []);
  const applySnapshot = useCallback((snapshot) => {
    historyRef.current.applying = true;
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setSettingBlocks(snapshot.settingBlocks);
    setSelectedEdgeId(null);
  }, [setNodes, setEdges]);
  useEffect(() => {
    if (!restoreSnapshot?.nodes || !restoreSnapshot?.edges) return;
    applySnapshot({
      nodes: restoreSnapshot.nodes,
      edges: restoreSnapshot.edges,
      settingBlocks: restoreSnapshot.settingBlocks ?? restoreSnapshot.settings ?? makeDefaultSettingBlocks()
    });
    setSelectedSection("universe");
  }, [restoreSnapshot, applySnapshot]);
  const commitCurrentSnapshot = useCallback(() => {
    const history = historyRef.current;
    const current = makeSnapshot();
    if (!history.present) { history.present = current; return; }
    if (snapshotKey(history.present) !== snapshotKey(current)) {
      history.past = [...history.past.slice(-49), history.present];
      history.present = current;
      history.future = [];
    }
  }, [makeSnapshot, snapshotKey]);
  const handleUndo = useCallback(() => {
    const history = historyRef.current;
    clearTimeout(history.timer);
    commitCurrentSnapshot();
    if (!history.past.length) return;
    const previous = history.past[history.past.length - 1];
    history.past = history.past.slice(0, -1);
    history.future = [history.present, ...history.future].slice(0, 50);
    history.present = previous;
    applySnapshot(previous);
  }, [applySnapshot, commitCurrentSnapshot]);
  const handleRedo = useCallback(() => {
    const history = historyRef.current;
    clearTimeout(history.timer);
    if (!history.future.length) return;
    const next = history.future[0];
    history.future = history.future.slice(1);
    history.past = [...history.past.slice(-49), history.present];
    history.present = next;
    applySnapshot(next);
  }, [applySnapshot]);

  useEffect(() => {
    const history = historyRef.current;
    clearTimeout(history.timer);
    if (history.applying) { history.applying = false; return undefined; }
    if (!history.present) { history.present = makeSnapshot(); return undefined; }
    history.timer = setTimeout(commitCurrentSnapshot, 180);
    return () => clearTimeout(history.timer);
  }, [nodes, edges, settingBlocks, makeSnapshot, commitCurrentSnapshot]);

  const onConnect = useCallback((connection) => setEdges((eds) => addEdge(connection, eds)), [setEdges]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.type === "execution"
          ? {
              ...node,
              data: {
                ...node.data,
                blocks: normalizeRankingBlocks(node.data?.blocks ?? [])
              }
            }
          : node
      )
    );
  }, [setNodes]);

  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(
        changes.filter((change) => {
          if (change.type !== "remove") return true;
          return !["u", "s1", "r1", "x1"].includes(change.id);
        })
      );
    },
    [onNodesChange]
  );

  const onEdgeClick = useCallback((evt, edge) => {
    evt.stopPropagation();
    setSelectedEdgeId(edge.id);
  }, []);

  const onPaneClick = useCallback(() => setSelectedEdgeId(null), []);

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = e.target?.tagName;
      const typing = tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;
      if (typing) return;

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") { e.preventDefault(); handleUndo(); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); handleRedo(); return; }

      if ((e.key === "Delete" || e.key === "Backspace") && selectedEdgeId) {
        setEdges((eds) => eds.filter((ed) => ed.id !== selectedEdgeId));
        setSelectedEdgeId(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedEdgeId, setEdges, handleUndo, handleRedo]);

  const decoratedEdges = useMemo(
    () =>
      edges.map((e) =>
        e.id === selectedEdgeId ? { ...e, style: { ...(e.style || {}), strokeWidth: 4 } } : e
      ),
    [edges, selectedEdgeId]
  );

  useEffect(() => onSelectedSectionChange(selectedSection), [selectedSection, onSelectedSectionChange]);
  const handleMatchModeChange = useCallback((nodeId, matchMode) => setNodes((current) =>
    current.map((node) => node.id === nodeId ? { ...node, data: { ...node.data, matchMode } } : node)
  ), [setNodes]);

  const renderedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          setNodes,
          selected: node.type === selectedSection,
          onSelect: setSelectedSection,
          onMatchModeChange: handleMatchModeChange
        }
      })),
    [nodes, setNodes, selectedSection, handleMatchModeChange]
  );

  const strategyDefinition = useMemo(() => buildStrategyDefinition(nodes, edges, settingBlocks), [nodes, edges, settingBlocks]);
  const deterministicSummary = useMemo(() => buildDeterministicStrategySummary(strategyDefinition), [strategyDefinition]);
  const handleSaveStrategy = useCallback(() => {
    const suggested = `Strategy ${new Date().toLocaleDateString()}`;
    const name = window.prompt("Name this strategy", suggested)?.trim();
    if (!name) return;
    try {
      const existing = JSON.parse(localStorage.getItem("stocksleuth:lab-strategies") || "[]");
      const saved = { id: createIdempotencyKey(), name, savedAt: new Date().toISOString(), strategy: strategyDefinition, nodes, edges, settings: settingBlocks };
      localStorage.setItem("stocksleuth:lab-strategies", JSON.stringify([saved, ...existing].slice(0, 50)));
      setSaveState("Saved");
      setTimeout(() => setSaveState(""), 2200);
    } catch { setSaveState("Could not save"); }
  }, [strategyDefinition, nodes, edges, settingBlocks]);
  const handleRunSimulation = useCallback(async () => {
    if (requestRef.current?.inProgress) return;
    const previous = requestRef.current;
    const idempotencyKey = previous?.networkFailed ? previous.idempotencyKey : createIdempotencyKey();
    requestRef.current = { idempotencyKey, inProgress: true, networkFailed: false };
    onBacktestUpdate({ key: idempotencyKey, status: "submitting", startDate, endDate, createdAt: new Date().toLocaleString() });
    setRunState({ submitting: true, error: "", status: "Submitting…" });
    try {
      const submitted = await submitBacktest({
        strategy: strategyDefinition,
        strategySummary: deterministicSummary,
        editorState: { version: 1, nodes, edges, settingBlocks },
        startDate,
        endDate,
        idempotencyKey
      });
      if (submitted?.credits) onCreditsChange(submitted.credits);
      const backtestId = submitted.backtestId ?? submitted.backtest_id ?? submitted.id;
      const pollUrl = submitted.pollUrl ?? submitted.poll_url;
      if (!backtestId) throw new Error("Backtest service returned no identifier.");
      requestRef.current.backtestId = backtestId;
      onBacktestUpdate({ key: idempotencyKey, id: backtestId, status: String(submitted.status ?? "queued").toLowerCase() });
      const poll = async () => {
        try {
          const result = await getBacktest(backtestId, pollUrl);
          const normalized = {
            ...normalizeBacktestResponse(result, strategyDefinition),
            deterministicSummary: result?.backtest?.strategy_summary ?? result?.strategy_summary ?? deterministicSummary,
            editorState: result?.backtest?.editor_state_json ?? result?.editor_state_json ?? { version: 1, nodes, edges, settingBlocks }
          };
          const status = normalized.status;
          setRunState({ submitting: !TERMINAL_BACKTEST_STATES.has(status), error: "", status });
          onBacktestUpdate({ key: idempotencyKey, id: backtestId, status, result: status === "completed" ? normalized : undefined });
          if (TERMINAL_BACKTEST_STATES.has(status)) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
            requestRef.current.inProgress = false;
            if (status === "completed") { onSimulationResult(normalized); onActivePanelChange("results"); }
            else setRunState({ submitting: false, status, error: `Backtest ${status}.` });
            return;
          }
          pollTimerRef.current = setTimeout(poll, BACKTEST_POLL_INTERVAL_MS);
        } catch (error) {
          requestRef.current.inProgress = false;
          requestRef.current.networkFailed = !error.response;
          setRunState({ submitting: false, status: "", error: getBacktestErrorMessage(error) });
          onBacktestUpdate({ key: idempotencyKey, id: backtestId, status: "failed", error: getBacktestErrorMessage(error) });
        }
      };
      await poll();
    } catch (error) {
      if (error.response?.data?.credits) onCreditsChange(error.response.data.credits);
      requestRef.current.inProgress = false;
      requestRef.current.networkFailed = !error.response;
      setRunState({ submitting: false, status: "", error: getBacktestErrorMessage(error) });
      onBacktestUpdate({ key: idempotencyKey, status: "failed", error: getBacktestErrorMessage(error) });
    }
  }, [strategyDefinition, deterministicSummary, nodes, edges, settingBlocks, startDate, endDate, onSimulationResult, onActivePanelChange, onBacktestUpdate, onCreditsChange]);

  return (
    <div style={flowWrapStyle}>
      <header style={canvasHeaderStyle}>
        <div style={settingsSummaryStyle}>
          <strong>{formatCurrency(safeNumber(getSettingValue(settingBlocks, "setting_initial_capital", 10000), 10000))}</strong>
          <span>·</span><span>{getSettingValue(settingBlocks, "setting_target_positions", 20)} stocks</span>
          <span>·</span><span>{Number(getSettingValue(settingBlocks, "setting_rebalance_days", 30)) === 30 ? "Monthly" : `${getSettingValue(settingBlocks, "setting_rebalance_days", 30)} days`}</span>
        </div>
        <div style={headerActionsStyle}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              enableAccessibleFieldDOMStructure={false}
              value={dayjs(startDate)}
              maxDate={dayjs(endDate)}
              onChange={(value) => value?.isValid() && setStartDate(value.format("YYYY-MM-DD"))}
              format="MMM D, YYYY"
              slotProps={backtestDatePickerSlots("Backtest start date")}
              sx={backtestDateFieldSx}
            />
            <DatePicker
              label="To"
              enableAccessibleFieldDOMStructure={false}
              value={dayjs(endDate)}
              minDate={dayjs(startDate)}
              maxDate={dayjs(today)}
              onChange={(value) => value?.isValid() && setEndDate(value.format("YYYY-MM-DD"))}
              format="MMM D, YYYY"
              slotProps={backtestDatePickerSlots("Backtest end date")}
              sx={backtestDateFieldSx}
            />
          </LocalizationProvider>
          <button type="button" style={settingsButtonStyle} onClick={() => setSettingsOpen(true)}>⚙ Settings</button>
          <button type="button" style={saveButtonStyle} onClick={handleSaveStrategy}>{saveState || "Save"}</button>
          <button type="button" style={runButtonStyle} disabled={runState.submitting || credits?.remaining === 0} onClick={handleRunSimulation}>{runState.submitting ? runState.status || "Running…" : credits?.remaining === 0 ? "No credits" : "▶ Run"}</button>
        </div>
      </header>
      {runState.error && <div role="alert" style={{ position: "absolute", zIndex: 21, top: 72, right: 20, padding: "9px 12px", background: "#fef2f2", color: "#991b1b" }}>{runState.error}</div>}
      <ReactFlow
        defaultEdgeOptions={defaultEdgeOptions}
        nodes={renderedNodes}
        edges={decoratedEdges}
        nodeTypes={NODE_TYPES}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeClick={onEdgeClick}
        onPaneClick={(event) => { onPaneClick(event); setSelectedSection(null); }}
        deleteKeyCode={null}
        fitView
        fitViewOptions={{
          padding: 0.08,
          minZoom: window.innerWidth <= 1366 ? 0.45 : 0.72,
          maxZoom: window.innerWidth <= 1366 ? 0.78 : 1
        }}
        minZoom={0.2}
        maxZoom={2}
        edgesFocusable
        edgesUpdatable={false}
        proOptions={{ hideAttribution: true }}
        resizeObserverOptions={{ box: "border-box" }}
      >
        <Background variant="dots" gap={18} size={1} color="#e5e7eb" />
      </ReactFlow>
      
      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} blocks={settingBlocks} setBlocks={setSettingBlocks} />
      <button
        style={playButtonStyle}
        title="Run simulated preview"
        onClick={handleRunSimulation}
        disabled={runState.submitting || credits?.remaining === 0}
      >
        ▶
      </button>
    </div>
  );
}

export default function LabFlow() {
  const [activePanel, setActivePanel] = useState("blocks");
  const [simulationResult, setSimulationResult] = useState(null);
  const [selectedSection, setSelectedSection] = useState("universe");
  const [historyRefreshVersion, setHistoryRefreshVersion] = useState(0);
  const [restoreSnapshot, setRestoreSnapshot] = useState(null);
  const [credits, setCredits] = useState(null);
  const [creditsError, setCreditsError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    getBacktestCredits({ signal: controller.signal })
      .then((payload) => {
        if (payload?.credits) setCredits(payload.credits);
        setCreditsError(false);
      })
      .catch((error) => {
        if (error.name !== "CanceledError" && error.code !== "ERR_CANCELED") setCreditsError(true);
      });
    return () => controller.abort();
  }, []);
  const handleBacktestUpdate = useCallback(() => setHistoryRefreshVersion((version) => version + 1), []);
  const handleOpenHistoryResult = useCallback((result) => {
    setSimulationResult(result);
    setActivePanel("results");
  }, []);
  const handleRestoreStrategy = useCallback((editorState) => {
    setRestoreSnapshot({ ...editorState, restoreId: createIdempotencyKey() });
    setActivePanel("blocks");
  }, []);

  return (
    <div
      className="labPage"
      style={{
        height: "100vh",
        paddingTop: 64,
        paddingRight: "var(--sidebar-width, 306px)",
        marginLeft: 1,
        background: "#f9fafb",
        boxSizing: "border-box",
        fontFamily: "Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
      }}
    >
      <div style={{ display: "flex", height: "100%", minWidth: 0, overflow: "hidden" }}>
        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <ReactFlowProvider>
            <FlowInner
              onSimulationResult={setSimulationResult}
              onActivePanelChange={setActivePanel}
              onSelectedSectionChange={setSelectedSection}
              onBacktestUpdate={handleBacktestUpdate}
              onCreditsChange={setCredits}
              credits={credits}
              restoreSnapshot={restoreSnapshot}
            />
          </ReactFlowProvider>
        </div>
        <LabSidebar
          activePanel={activePanel}
          setActivePanel={setActivePanel}
          simulationResult={simulationResult}
          selectedSection={selectedSection}
          historyRefreshVersion={historyRefreshVersion}
          onOpenHistoryResult={handleOpenHistoryResult}
          onRestoreStrategy={handleRestoreStrategy}
        />
      </div>
      <div
        style={creditsUsedStyle}
        aria-label={credits ? `Credits used: ${credits.used} of ${credits.limit}` : "Loading backtest credits"}
        title={credits?.resetsAt ? `${credits.tier} tier; resets ${new Date(credits.resetsAt).toLocaleString()}` : "Backtest credits"}
      >
        <span style={creditsUsedLabelStyle}>Credits used</span>
        <strong style={creditsUsedValueStyle}>{credits ? `${credits.used} / ${credits.limit}` : creditsError ? "Unavailable" : "Loading…"}</strong>
      </div>
      <LabFirstVisitTour />
    </div>
  );
}

/* =====================================================
   STYLES
===================================================== */

const nodeStyle = (accent, selected = false) => ({
  width: 270,
  padding: 12,
  background: `${accent}14`,
  borderRadius: 12,
  border: `${selected ? 3 : 2}px ${selected ? "solid" : "dashed"} ${accent}`,
  boxShadow: selected ? `0 0 0 4px ${accent}20, 0 14px 30px rgba(15,23,42,.12)` : "none"
});

const nodeHeaderStyle = (accent) => ({
  display: "flex",
  flexDirection: "column",
  fontWeight: 700,
  color: accent,
  marginBottom: 10,
  fontSize: 13
});

const nodeSubtitleStyle = { color: "#64748b", fontSize: 10, fontWeight: 500, marginTop: 2 };
const matchModeStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 10, color: "#475569" };
const canvasHeaderStyle = { position: "absolute", zIndex: 20, top: 14, left: 20, right: 20, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 14, background: "rgba(255,255,255,.94)", border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(15,23,42,.08)" };
const settingsSummaryStyle = { display: "flex", alignItems: "center", gap: 8, color: "#475569", fontSize: 12, flexWrap: "wrap" };
const headerActionsStyle = { display: "flex", gap: 8, flexShrink: 0 };
const backtestDatePickerSlots = (ariaLabel) => ({
  textField: { size: "small", inputProps: { "aria-label": ariaLabel } },
  desktopPaper: {
    sx: {
      mt: 1,
      borderRadius: "16px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 18px 48px rgba(15,23,42,.16)",
      "& .MuiPickersDay-root.Mui-selected": { backgroundColor: "#5b35df" },
      "& .MuiPickersDay-root.Mui-selected:hover": { backgroundColor: "#4e27d8" },
      "& .MuiPickersDay-today": { borderColor: "#8b5cf6" }
    }
  }
});
const backtestDateFieldSx = {
  width: 148,
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    fontFamily: "Poppins, system-ui, sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: "#334155",
    "& fieldset": { borderColor: "#dbe3ee" },
    "&:hover fieldset": { borderColor: "#a78bfa" },
    "&.Mui-focused fieldset": { borderColor: "#6d4aff", borderWidth: 1.5 }
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Poppins, system-ui, sans-serif",
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b"
  },
  "& .MuiInputBase-input": { padding: "8px 9px", fontSize: 11 },
  "& .MuiInputAdornment-root": { marginLeft: 0 },
  "& .MuiIconButton-root": { color: "#6d4aff", padding: "4px" },
  "& .MuiIconButton-root svg": { width: 17, height: 17 }
};
const settingsButtonStyle = { border: "1px solid #cbd5e1", background: "#fff", color: "#334155", borderRadius: 10, padding: "8px 11px", fontWeight: 700, cursor: "pointer" };
const saveButtonStyle = { ...settingsButtonStyle, borderColor: "#c4b5fd", color: "#5b21b6", background: "#faf5ff" };
const runButtonStyle = { ...settingsButtonStyle, background: "#4E27D8", color: "#fff", borderColor: "#4E27D8" };
const exitNoticeStyle = { position: "absolute", zIndex: 7, right: 24, bottom: 18, maxWidth: 430, padding: "8px 10px", background: "#faf5ff", border: "1px solid #ddd6fe", borderRadius: 10, color: "#6d28d9", fontSize: 10, fontWeight: 650 };
const drawerOverlayStyle = { position: "fixed", zIndex: 2147483647, inset: 0, width: "100vw", height: "100dvh", background: "rgba(15,23,42,.34)", display: "flex", justifyContent: "flex-end", alignItems: "stretch", fontFamily: "Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif" };
const drawerStyle = { width: "min(440px, 94vw)", maxWidth: "100vw", height: "100%", boxSizing: "border-box", background: "#fff", boxShadow: "-18px 0 50px rgba(15,23,42,.2)", display: "flex", flexDirection: "column" };
const drawerHeaderStyle = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: 20, borderBottom: "1px solid #e2e8f0" };
const drawerTitleStyle = { fontSize: 18, color: "#0f172a" };
const drawerSubtitleStyle = { fontSize: 11, color: "#64748b", marginTop: 3 };
const drawerCloseStyle = { border: "none", background: "#f1f5f9", borderRadius: 9, width: 32, height: 32, cursor: "pointer", fontSize: 20 };
const drawerBodyStyle = { padding: 20, overflowY: "auto" };
const drawerFieldsStyle = { display: "grid", gap: 12 };
const drawerFieldStyle = { display: "grid", gridTemplateColumns: "minmax(0,1fr) 150px", alignItems: "center", gap: 12, fontSize: 11, fontWeight: 650, color: "#334155" };
const drawerInputStyle = { width: "100%", boxSizing: "border-box", border: "1px solid #cbd5e1", borderRadius: 9, padding: "8px 9px", background: "#fff", color: "#0f172a" };
const advancedBadgeStyle = { display: "block", color: "#94a3b8", fontSize: 8, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 2 };
const tourOverlayStyle = { position: "fixed", zIndex: 2147483647, inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, boxSizing: "border-box", background: "rgba(15,23,42,.52)", backdropFilter: "blur(5px)", fontFamily: "Poppins, system-ui, sans-serif" };
const tourCardStyle = { width: "min(430px, 100%)", boxSizing: "border-box", padding: 24, borderRadius: 20, background: "#fff", boxShadow: "0 30px 80px rgba(15,23,42,.3)", animation: "labTourIn .24s ease-out" };
const tourProgressStyle = { display: "grid", gridTemplateColumns: `repeat(${LAB_TOUR_STEPS.length}, 1fr)`, gap: 6, marginBottom: 22 };
const tourProgressDotStyle = (active) => ({ height: 4, borderRadius: 4, background: active ? "#6d4aff" : "#e2e8f0", transition: "background .2s ease" });
const tourIllustrationStyle = { width: 58, height: 58, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, borderRadius: 18, background: "linear-gradient(135deg,#ede9fe,#ddd6fe)", color: "#5b21b6", fontSize: 24, fontWeight: 800, transform: "rotate(-3deg)" };
const tourTitleStyle = { margin: "0 0 8px", color: "#0f172a", fontSize: 23, lineHeight: 1.2 };
const tourTextStyle = { margin: 0, minHeight: 66, color: "#475569", fontSize: 13, lineHeight: 1.65 };
const tourActionsStyle = { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, marginTop: 22 };
const tourSkipStyle = { marginRight: "auto", border: 0, background: "transparent", color: "#64748b", padding: "8px 0", fontFamily: "inherit", fontWeight: 650, cursor: "pointer" };
const tourSecondaryStyle = { border: "1px solid #dbe3ee", borderRadius: 9, background: "#fff", color: "#334155", padding: "8px 12px", fontFamily: "inherit", fontWeight: 700, cursor: "pointer" };
const tourPrimaryStyle = { ...tourSecondaryStyle, borderColor: "#5b21b6", background: "#5b21b6", color: "#fff" };
const paletteContextStyle = { marginBottom: 14, padding: "9px 10px", borderRadius: 10, background: "#f8fafc", color: "#64748b", fontSize: 10, lineHeight: 1.4 };

const blocksContainerStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 8,
  minHeight: 44
};

const miniBlockStyle = (accent) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px",
  borderRadius: 11,
  background: `${accent}34`,
  border: "1px solid transparent",
  boxShadow: `0 3px 10px ${accent}16`,
  color: "#111827",
  fontWeight: 500,
  cursor: "grab",
  userSelect: "none"
});

const fixedSettingBlockStyle = (accent) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "8px",
  borderRadius: 11,
  background: `${accent}2c`,
  border: "1px solid transparent",
  boxShadow: `0 3px 10px ${accent}14`,
  color: "#111827",
  fontWeight: 600,
  cursor: "default",
  userSelect: "none"
});

const operatorMiniBlockStyle = (accent) => ({
  ...miniBlockStyle(accent),
  background: `${accent}24`,
  boxShadow: `0 3px 10px ${accent}12`
});

const blockContentStyle = {
  flex: "1 1 auto",
  minWidth: 0
};

const blockAccentDotStyle = (accent) => ({
  width: 8,
  height: 8,
  flex: "0 0 8px",
  alignSelf: "center",
  borderRadius: "50%",
  background: accent,
  boxShadow: `0 0 0 3px ${accent}26`
});

const operatorRowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
  alignItems: "center",
  gap: 8,
  width: "100%",
  minWidth: 0
};

const operatorSymbolStyle = {
  fontWeight: 650,
  minWidth: 18,
  textAlign: "center",
  fontSize: 11,
  color: "#0f172a",
  lineHeight: 1
};

const blockHeaderRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  width: "100%"
};

const blockTitleWrapStyle = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  minWidth: 0,
  flex: "1 1 auto"
};

const dragGripStyle = {
  color: "#cbd5e1",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.12em",
  lineHeight: 1
};

const blockFieldStackStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  width: "100%",
  minWidth: 0
};

const blockFieldTitleStyle = {
  fontWeight: 700,
  fontSize: 11,
  lineHeight: 1.2,
  color: "#0f172a",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const blockFieldRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10
};

const blockFieldLabelStyle = {
  fontSize: 10,
  fontWeight: 700,
  minWidth: 66,
  color: "#334155",
  textTransform: "uppercase",
  letterSpacing: "0.04em"
};

const inputBlockStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  width: "100%"
};

const inputBlockInputStyle = {
  width: "100%",
  border: "1px solid rgba(148,163,184,0.32)",
  outline: "none",
  background: "#ffffff",
  borderRadius: 8,
  padding: "7px 8px",
  fontWeight: 600,
  fontSize: 11,
  color: "#0f172a"
};

const compactBlockRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  minWidth: 0
};

const compactBlockTitleStyle = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  minWidth: 0,
  flex: "1 1 auto"
};

const compactBlockControlStyle = {
  flex: "0 0 96px",
  minWidth: 96
};

const scratchBlockRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  width: "100%",
  minWidth: 0,
  flexWrap: "wrap"
};

const scratchBlockTextStyle = {
  //fontWeight: 750,
  fontSize: 11,
  lineHeight: 1.15,
  color: "#0f172a",
  whiteSpace: "nowrap"
};

const scratchBlockInputWrapStyle = {
  flex: "1 1 96px",
  minWidth: 92,
  maxWidth: 124
};

const universeFilterRowStyle = {
  display: "grid",
  gridTemplateColumns: "82px minmax(0, 1fr)",
  alignItems: "center",
  gap: 7,
  width: "100%",
  minWidth: 0
};

const universeFilterInputWrapStyle = {
  width: "100%",
  minWidth: 0,
  justifySelf: "end"
};

const topFilterRowStyle = {
  display: "grid",
  gridTemplateColumns: "auto 64px minmax(0, 1fr)",
  alignItems: "center",
  gap: 7,
  width: "100%",
  minWidth: 0
};

const topFilterInputWrapStyle = {
  width: 64,
  minWidth: 64
};

const settingInlineRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 4,
  width: "100%",
  minWidth: 0
};

const settingInlineTitleStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
  flex: "1 1 auto"
};

const settingInlineLabelStyle = {
  //fontWeight: 650,
  fontSize: 11,
  lineHeight: 1.1,
  color: "#0f172a",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const settingInlineControlStyle = {
  flex: "0 0 96px",
  minWidth: 96
};

const settingInlineInputStyle = {
  width: "100%",
  height: 30,
  border: "1px solid rgba(148,163,184,0.32)",
  outline: "none",
  background: "#f8fafc",
  borderRadius: 9,
  padding: "0 10px",
  fontWeight: 700,
  fontSize: 12,
  color: "#0f172a",
  fontVariantNumeric: "tabular-nums",
  boxSizing: "border-box"
};

const argSlotStyle = (accent) => ({
  flex: 1,
  minWidth: 0,
  minHeight: 30,
  borderRadius: 8,
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(255,255,255,0.8)",
  boxShadow: "0 1px 3px rgba(15,23,42,0.07)",
  display: "flex",
  alignItems: "center",
  padding: "0 3px",
  overflow: "hidden"
});

const argInputStyle = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  fontWeight: 500,
  fontSize: 10,
  color: "#0f172a",
  fontVariantNumeric: "tabular-nums"
};

const argInputWrapStyle = { position: "relative", width: "100%", minWidth: 0, display: "flex", alignItems: "center" };
const argInputSuffixStyle = { position: "absolute", right: 3, color: "#64748b", fontSize: 10, fontWeight: 700, pointerEvents: "none" };

const argPillStyle = (accent) => ({
  display: "flex",
  alignItems: "center",
  gap: 0,
  width: "100%",
  minWidth: 0,
  padding: "4px 5px",
  borderRadius: 6,
  background: "transparent",
  color: "#0f172a",
  fontWeight: 600,
  fontSize: 10,
  cursor: "grab",
  userSelect: "none",
  overflow: "hidden"
});

const argPillLabelStyle = {
  display: "block",
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  lineHeight: 1.2
};

const argClearStyle = {
  display: "none",
  marginLeft: "auto",
  border: "1px solid rgba(15,23,42,0.12)",
  background: "#ffffff",
  width: 20,
  height: 20,
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: 800,
  lineHeight: "18px",
  color: "#475569"
};

const trashButtonStyle = {
  flex: "0 0 22px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.88)",
  border: "none",
  borderRadius: "50%",
  width: 22,
  height: 22,
  cursor: "pointer",
  fontSize: 0,
  lineHeight: 1,
  color: "transparent"
};

const removeButtonTextStyle = {
  color: "#64748b",
  fontSize: 12,
  fontWeight: 800,
  lineHeight: 1
};

const handleStyle = (accent) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: accent,
  border: "2px solid #0b0b0b"
});

const paletteStyle = {
  width: "clamp(220px, 24vw, 307px)",
  flex: "0 0 clamp(220px, 24vw, 307px)",
  minWidth: 0,
  padding: "0 14px 18px",
  background: "#ffffff",
  borderLeft: "1px solid #e5e7eb",
  boxSizing: "border-box",
  overflowY: "auto",
  overflowX: "hidden"
};

const paletteContentStyle = {
  paddingTop: 2
};

const sidebarTopStyle = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 8,
  padding: "14px 14px 12px",
  margin: "0 -14px 12px",
  background: "#ffffff",
  borderBottom: "1px solid rgba(226,232,240,0.9)",
  boxShadow: "0 10px 20px rgba(15,23,42,0.045)"
};

const sidebarTabStyle = (active) => ({
  height: 34,
  fontFamily: "Lexend",
  borderRadius: 12,
  border: active
    ? "1px solid #4e27d8"
    : "1px solid rgba(148, 163, 184, 0.22)",
  background: active
    ? "#4e27d8"
    : "rgba(248, 250, 252, 0.9)",
  color: active
    ? "#ffffff"
    : "#64748b",
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "0.01em",
  cursor: "pointer",
  boxShadow: active
    ? "rgba(78, 39, 216, 0.18) 0px 1px 16px"
    : "none",
  transition: "background-color 0.2s ease-in-out",
});

const resultsContentStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  width: "100%",
  minWidth: 0,
  paddingBottom: 16
};

const historyContentStyle = { display: "flex", flexDirection: "column", gap: 16, width: "100%", minWidth: 0, boxSizing: "border-box", paddingBottom: 12 };
const historyHeaderStyle = { display: "flex", alignItems: "flex-end", justifyContent: "space-between", padding: "6px 2px 2px" };
const historyTitleStyle = { fontSize: 20, lineHeight: 1.15, letterSpacing: "-.025em", fontWeight: 750, color: "#0f172a" };
const historyCountStyle = { minWidth: 28, height: 28, padding: "0 8px", borderRadius: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#ede9fe", color: "#5b21b6", fontSize: 11, fontWeight: 800 };
const historyListStyle = { display: "flex", flexDirection: "column", gap: 14, width: "100%", minWidth: 0 };
const historyRunStyle = (enabled) => ({ width: "100%", minWidth: 0, boxSizing: "border-box", overflow: "hidden", padding: "15px", borderRadius: 14, border: "1px solid #e2e8f0", background: "#fff", textAlign: "left", fontFamily: "inherit", color: "#334155", cursor: enabled ? "pointer" : "default", opacity: 1, boxShadow: enabled ? "0 3px 12px rgba(15,23,42,.045)" : "none" });
const historyRunTopStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 };
const historyStatusStyle = (status) => ({ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: status === "completed" ? "#15803d" : status === "failed" || status === "cancelled" || status === "canceled" ? "#b91c1c" : "#a16207" });
const historyStatusDotStyle = (status) => ({ width: 7, height: 7, borderRadius: "50%", background: status === "completed" ? "#22c55e" : status === "failed" || status === "cancelled" || status === "canceled" ? "#ef4444" : "#f59e0b", boxShadow: status === "running" || status === "pending" || status === "queued" ? "0 0 0 3px #fef3c7" : "none" });
const historyDateStyle = { minWidth: 0, maxWidth: "48%", overflow: "hidden", textOverflow: "ellipsis", fontSize: 9, color: "#94a3b8", whiteSpace: "nowrap", textAlign: "right" };
const historyPeriodStyle = { marginTop: 5, fontSize: 9, fontWeight: 550, color: "#64748b", fontVariantNumeric: "tabular-nums" };
const historyRunBottomStyle = { display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: "7px 10px", margin: "12px 0 0", padding: "11px 0 0", borderTop: "1px solid #eef2f7", fontSize: 9, color: "#94a3b8" };
const historyOpenStyle = { marginLeft: "auto", color: "#6d4aff", fontWeight: 750 };
const historyErrorStyle = { marginTop: 8, paddingTop: 8, borderTop: "1px solid #fee2e2", color: "#b91c1c", fontSize: 9, lineHeight: 1.4 };
const historyNameRowStyle = { display: "flex", alignItems: "center", gap: 8, marginTop: 9 };
const historyNameStyle = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#0f172a", fontSize: 13, lineHeight: 1.25 };
const historyRenameStyle = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: 5, marginTop: 9 };
const historyRenameInputStyle = { minWidth: 0, height: 30, boxSizing: "border-box", border: "1px solid #a78bfa", borderRadius: 7, padding: "0 8px", fontFamily: "inherit", fontSize: 10 };
const historyActionButtonStyle = { border: "1px solid #dbe3ee", borderRadius: 7, background: "#fff", color: "#475569", padding: "4px 7px", fontFamily: "inherit", fontSize: 9, fontWeight: 700, cursor: "pointer" };
const historyTextButtonStyle = { minHeight: 26, border: 0, background: "transparent", color: "#6d4aff", padding: "5px 1px", fontFamily: "inherit", fontSize: 8.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" };
const historyOpenButtonStyle = { ...historyTextButtonStyle, marginRight: "auto", color: "#fff", background: "#5b35df", borderRadius: 7, padding: "6px 9px" };
const historyDeleteButtonStyle = { ...historyTextButtonStyle, color: "#b91c1c" };
const historySrOnlyStyle = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0 };
const historyFilterStyle = { display: "flex", flexDirection: "column", alignItems: "stretch", gap: 6, width: "100%", minWidth: 0, boxSizing: "border-box", fontSize: 9, fontWeight: 700, color: "#64748b" };
const historyStatusSelectSx = {
  width: "auto",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  "& .MuiSelect-select": { display: "flex", alignItems: "center", paddingTop: "7px", paddingBottom: "7px", fontSize: 10, fontWeight: 700 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe3ee" }
};
const historyMetricsStyle = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 4, width: "100%", minWidth: 0, boxSizing: "border-box", marginTop: 11, padding: "9px 7px", borderRadius: 9, background: "#f8fafc", color: "#94a3b8", fontSize: 7.5, textAlign: "center" };
const historyMetricItemStyle = { display: "flex", minWidth: 0, flexDirection: "column", gap: 3, overflow: "hidden" };
const historyErrorStateStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: 10, border: "1px solid #fecaca", borderRadius: 9, background: "#fef2f2", color: "#991b1b", fontSize: 10, lineHeight: 1.4 };
const historyRetryStyle = { border: "1px solid #fca5a5", borderRadius: 7, background: "#fff", color: "#991b1b", padding: "5px 8px", fontFamily: "inherit", fontSize: 9, fontWeight: 750, cursor: "pointer" };
const historySkeletonListStyle = { display: "flex", flexDirection: "column", gap: 12, width: "100%" };
const historySkeletonCardStyle = { padding: "15px", borderRadius: 14, border: "1px solid #e2e8f0", background: "#fff" };
const historySkeletonTopStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 };
const historySkeletonMetricsStyle = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 6, marginTop: 11 };
const historyRunningStyle = { display: "grid", gridTemplateColumns: "18px minmax(0, 1fr)", alignItems: "center", gap: 8, marginTop: 12, padding: "10px 11px", borderRadius: 10, background: "#f5f3ff", color: "#4c1d95", fontSize: 10 };
const historyRunningTitleStyle = { display: "block", fontSize: 10, lineHeight: 1.3 };
const historyRunningTextStyle = { display: "block", marginTop: 2, color: "#7c6aa6", fontSize: 8.5, lineHeight: 1.35 };
const historyPaginationStyle = { position: "sticky", bottom: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 0", background: "#fff", color: "#64748b", fontSize: 9 };
const historyPageButtonStyle = { minHeight: 32, border: "1px solid #dbe3ee", borderRadius: 8, background: "#f8fafc", color: "#334155", padding: "5px 9px", fontFamily: "inherit", fontSize: 9, fontWeight: 700, cursor: "pointer" };

const resultsHeroStyle = {
  display: "block",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "16px 14px",
  borderRadius: 13,
  background: "linear-gradient(145deg,#f7f5ff,#fbfdff)",
  border: "1px solid #e2e8f0",
  boxShadow: "0 2px 7px rgba(15,23,42,0.04)"
};

const resultsHeroMainStyle = { minWidth: 0, textAlign: "center" };

const resultsEyebrowStyle = {
  fontSize: 10,
  fontWeight: 650,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#94a3b8",
  marginBottom: 4
};

const resultsTitleStyle = {
  fontSize: 18,
  fontWeight: 700,
  color: "#0f172a",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "left"
};

const resultsGeneratedStyle = {
  fontSize: 10,
  fontWeight: 500,
  color: "#64748b",
  marginTop: 5,
  lineHeight: 1.35,
  textAlign: "left"
};

const resultsSectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginTop: 7,
  padding: "0 2px",
  color: "#0f172a",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap"
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 1,
  overflow: "hidden",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  background: "#e2e8f0"
};

const secondaryMetricGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 1,
  overflow: "hidden",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  background: "#e2e8f0"
};

const metricCardStyle = (tone = "neutral") => ({
  minWidth: 0,
  minHeight: 64,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "10px 11px",
  borderRadius: 0,
  background: tone === "positive" ? "#f6fef8" : tone === "negative" ? "#fffaf5" : "#ffffff",
  border: 0,
  textAlign: "left"
});

const metricLabelStyle = {
  fontSize: 8.5,
  fontWeight: 600,
  color: "#64748b",
  marginBottom: 4,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};
const metricLabelRowStyle = { position: "relative", display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 5, width: "100%", minWidth: 0, minHeight: 15 };
const metricHelpWrapStyle = { position: "relative", display: "inline-flex", flex: "0 0 auto" };
const metricHelpButtonStyle = { width: 15, height: 15, padding: 0, borderRadius: "50%", border: "1px solid #cbd5e1", background: "#fff", color: "#64748b", fontFamily: "serif", fontSize: 10, fontWeight: 800, lineHeight: "13px", cursor: "help" };
const metricHelpTooltipStyle = ({ left, top }) => ({ position: "fixed", zIndex: 1000, left, top, transform: "translateY(-100%)", width: 210, boxSizing: "border-box", padding: "10px 11px", borderRadius: 9, background: "#0f172a", color: "#fff", boxShadow: "0 12px 30px rgba(15,23,42,.24)", fontFamily: "Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif", fontSize: 10, fontWeight: 400, lineHeight: 1.55, letterSpacing: "0.005em", textAlign: "left", whiteSpace: "normal", pointerEvents: "none" });

const metricValueStyle = {
  fontSize: 17,
  fontWeight: 700,
  color: "#0f172a",
  lineHeight: 1.1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const metricHintStyle = {
  marginTop: 4,
  fontSize: 9,
  color: "#94a3b8",
  maxWidth: "100%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const equityChartBoxStyle = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: 10,
  borderRadius: 10,
  background: "#ffffff",
  color: "#334155",
  boxShadow: "0 2px 7px rgba(15,23,42,0.045)",
  border: "1px solid #e2e8f0"
};

const equityChartHeaderStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  alignItems: "center",
  gap: 8,
  marginBottom: 8,
  fontSize: 10,
  fontWeight: 600,
  color: "#475569",
  whiteSpace: "nowrap"
};

const benchmarkTagsStyle = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  flexWrap: "wrap",
  marginBottom: 9
};

const benchmarkTagStyle = (active, color) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  minHeight: 25,
  padding: "4px 8px",
  borderRadius: 999,
  border: `1px solid ${active ? color : "#e2e8f0"}`,
  background: active ? `${color}14` : "#ffffff",
  color: active ? "#334155" : "#94a3b8",
  fontFamily: "inherit",
  fontSize: 9,
  fontWeight: 700,
  cursor: "pointer"
});

const benchmarkTagDotStyle = (color) => ({
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: color
});

const equityChartInnerStyle = {
  position: "relative",
  height: 108,
  borderRadius: 6,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  overflow: "hidden"
};

const equitySvgStyle = {
  width: "100%",
  height: "100%",
  display: "block",
  color: "#4e7de9"
};

const emptyChartOverlayStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#94a3b8",
  fontSize: 10
};

const equityChartFooterStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: 8,
  fontSize: 10,
  color: "#94a3b8",
  fontWeight: 550
};

const strategySummaryStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 13,
  borderRadius: 8,
  background: "#f8fafc",
  border: "1px solid #e2e8f0"
};

const strategySummaryTitleStyle = {
  fontSize: 12,
  fontWeight: 650,
  color: "#0f172a",
  marginBottom: 5,
  paddingBottom: 7,
  borderBottom: "1px solid #e2e8f0"
};

const summaryRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  fontSize: 11,
  color: "#64748b"
};

const summaryValueStyle = {
  maxWidth: "58%",
  color: "#334155",
  fontWeight: 550,
  textAlign: "right",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const emptyResultsStyle = {
  minHeight: 420,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: 24,
  borderRadius: 18,
  background: "#f8fafc",
  border: "1px dashed rgba(148,163,184,0.44)"
};

const emptyResultsIconStyle = {
  width: 54,
  height: 54,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#111827",
  color: "#ffffff",
  fontSize: 20,
  fontWeight: 900,
  marginBottom: 14
};

const emptyHistoryIconStyle = { ...emptyResultsIconStyle, background: "#ede9fe", color: "#6d28d9" };

const emptyResultsTitleStyle = {
  fontSize: 16,
  fontWeight: 850,
  color: "#0f172a",
  marginBottom: 7
};

const emptyResultsTextStyle = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.55,
  color: "#64748b",
  maxWidth: 250
};

const paletteHeader = (accent) => ({
  fontWeight: 750,
  color: accent,
  marginBottom: 9,
  fontSize: 11,
  letterSpacing: "0.055em",
  textTransform: "uppercase"
});

const paletteGrid = (groupKey) => ({
  display: "grid",
  gridTemplateColumns:
    groupKey === "technicals" || groupKey === "fundamentals"
      ? "repeat(auto-fit, minmax(120px, 1fr))"
      : "minmax(0, 1fr)",
  gap: 9
});

const paletteMiniBlockStyle = (accent, groupKey) => ({
  padding: "8px",
  borderRadius: 11,
  background: `${accent}38`,
  border: "1px solid transparent",
  boxShadow: `0 3px 10px ${accent}18`,
  color: "#0f172a",
  fontWeight: 650,
  cursor: "grab",
  userSelect: "none",
  transition: "transform 160ms ease, box-shadow 160ms ease, background 160ms ease",
  minHeight: groupKey === "operators" ? 36 : 34,
  boxSizing: "border-box"
});

const paletteMiniBlockInnerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 6,
  height: "100%",
  width: "100%",
  minWidth: 0
};

const paletteOperatorPreviewStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
  alignItems: "center",
  gap: 7,
  width: "100%",
  minWidth: 0
};

const paletteArgSlotPreviewStyle = {
  minWidth: 0,
  height: 24,
  padding: "0 7px",
  borderRadius: 8,
  background: "#f8fafc",
  border: "1px solid rgba(148,163,184,0.32)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#6d28d9",
  fontSize: 10,
  fontWeight: 750,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const paletteOperatorSymbolStyle = {
  minWidth: 24,
  textAlign: "center",
  color: "#0f172a",
  fontSize: 12,
  fontWeight: 850,
  lineHeight: 1
};

const paletteScratchRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "nowrap",
  minWidth: 0
};

const paletteScratchInputPillStyle = {
  minWidth: 34,
  height: 20,
  padding: "0 8px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.72)",
  border: "1px solid rgba(15,23,42,0.08)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 800,
  color: "#0f172a"
};

const paletteBlockLabelStyle = {
  minWidth: 0,
  fontSize: 9,
  fontWeight: 600,
  lineHeight: 1.15,
  textAlign: "left",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const flowWrapStyle = {
  position: "relative",
  width: "100%",
  height: "100%"
};

const strategyNarrativeStyle = {
  padding: 13,
  borderRadius: 8,
  background: "#f5f3ff",
  border: "1px solid #ddd6fe"
};

const strategyNarrativeTextStyle = {
  margin: 0,
  color: "#475569",
  fontSize: 11,
  lineHeight: 1.65,
  textAlign: "justify"
};

const creditsUsedStyle = {
  position: "fixed",
  left: 18,
  bottom: 18,
  zIndex: 20,
  display: "inline-flex",
  alignItems: "center",
  gap: 9,
  padding: "8px 11px",
  border: "1px solid #e2e8f0",
  borderRadius: 11,
  background: "rgba(255,255,255,0.94)",
  boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
  backdropFilter: "blur(8px)",
  fontVariantNumeric: "tabular-nums"
};

const creditsUsedLabelStyle = {
  color: "#64748b",
  fontSize: 10,
  fontWeight: 650
};

const creditsUsedValueStyle = {
  color: "#4e27d8",
  fontSize: 11,
  fontWeight: 800
};

const playButtonStyle = {
  display: "none",
  position: "fixed",
  right: 628,
  bottom: 28,
  width: 58,
  height: 58,
  borderRadius: 18,
  background: "#4E27D8",
  color: "#ffffff",
  border: "none",
  fontSize: 24,
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "rgba(15, 23, 42, 0.35) 0px 5px 30px",
  zIndex: 10
};

